/**
 * Custom-elements implementation for the product step builder used on AI image products.
 *
 * Architecture:
 * - Small element classes such as `BtnNext`, `OptionItem`, and `ExtraAdd` emit semantic
 *   DOM events instead of owning shared state.
 * - `StepBlock` acts as the coordinator for the whole experience: it resolves the active
 *   Shopify variant, manages the visible step flow, tracks uploaded media and generated
 *   preview state, and assembles the final cart payload.
 * - Supporting drawer/summary elements mirror the current design selection UI for mobile
 *   and multi-option flows, while top-level helpers handle page protection, interaction
 *   locks, checkout-return resets, and other cross-cutting concerns.
 */



const PAGE_PROTECTION_ENABLED = true;
const CHECKOUT_RETURN_RESET_KEY = 'step_builder_reset_after_checkout';
const STEP_BUILDER_STORAGE_KEYS = [
  'custom_product',
  'extra_title',
  'm_products',
  'step_header',
  'step_header_style'
];

/**
 * Installs basic client-side interaction guards that discourage right-click and common
 * developer-tool shortcuts on pages where the builder is rendered.
 *
 * Expects `document.body` to be available before binding the one-time listeners.
 *
 * @returns {void}
 */
function installPageProtection() {
  if (!PAGE_PROTECTION_ENABLED || document.body?.dataset.pageProtectionBound === 'true') {
    return;
  }

  const blockedShortcuts = [
    event => event.key === 'F12',
    event => event.ctrlKey && event.shiftKey && ['I', 'J', 'C'].includes(event.key.toUpperCase()),
    event => event.ctrlKey && event.key.toUpperCase() === 'U'
  ];

  document.addEventListener('contextmenu', event => {
    event.preventDefault();
  }, true);

  document.addEventListener('keydown', event => {
    const shouldBlock = blockedShortcuts.some(matcher => matcher(event));
    if (!shouldBlock) return;

    event.preventDefault();
    event.stopPropagation();
  }, true);

  document.body.dataset.pageProtectionBound = 'true';
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', installPageProtection, { once: true });
} else {
  installPageProtection();
}

/**
 * Creates or reuses the full-page overlay used to temporarily block pointer interaction
 * while long-running builder actions such as generation or checkout submission are active.
 *
 * @returns {HTMLDivElement}
 */
function ensurePageInteractionLock() {
  let overlay = document.querySelector('[data-page-interaction-lock]');
  if (overlay) return overlay;

  overlay = document.createElement('div');
  overlay.setAttribute('data-page-interaction-lock', 'true');
  overlay.setAttribute('aria-hidden', 'true');
  overlay.style.cssText = [
    'position:fixed',
    'inset:0',
    'z-index:2147483647',
    'background:transparent',
    'display:none',
    'cursor:progress',
    'pointer-events:auto',
    'touch-action:none'
  ].join(';');

  const swallowEvent = event => {
    event.preventDefault();
    event.stopPropagation();
  };

  ['click', 'mousedown', 'mouseup', 'pointerdown', 'pointerup', 'touchstart', 'touchend'].forEach(eventName => {
    overlay.addEventListener(eventName, swallowEvent, true);
  });

  document.body.appendChild(overlay);
  return overlay;
}

/**
 * Adds or removes a named interaction lock reason and updates the shared overlay/UI state.
 *
 * Expects `reason` to be a stable string so multiple async flows can coordinate safely.
 *
 * @param {string} reason Unique lock identifier for the current async flow.
 * @param {boolean} isBlocked Whether the lock should be active.
 * @returns {void}
 */
function setPageInteractionBlocked(reason, isBlocked) {
  if (!document.body) return;

  const activeReasons = new Set(
    (document.body.dataset.pageInteractionLocks || '')
      .split('|')
      .map(value => value.trim())
      .filter(Boolean)
  );

  if (isBlocked) {
    activeReasons.add(reason);
  } else {
    activeReasons.delete(reason);
  }

  const nextValue = Array.from(activeReasons).join('|');
  if (nextValue) {
    document.body.dataset.pageInteractionLocks = nextValue;
  } else {
    delete document.body.dataset.pageInteractionLocks;
  }

  const overlay = ensurePageInteractionLock();
  const hasLocks = activeReasons.size > 0;

  overlay.style.display = hasLocks ? 'block' : 'none';
  document.body.classList.toggle('page-interaction-blocked', hasLocks);
  document.body.style.cursor = hasLocks ? 'progress' : '';
}

/**
 * Flags the session so the step-builder state is cleared when the customer returns from checkout.
 *
 * @returns {void}
 */
function markStepBuilderForCheckoutReset() {
  try {
    window.sessionStorage.setItem(CHECKOUT_RETURN_RESET_KEY, 'true');
  } catch (error) {
    console.warn('Unable to persist checkout reset flag.', error);
  }
}

/**
 * Removes the step-builder data persisted in local storage across page loads.
 *
 * @returns {void}
 */
function clearStepBuilderStoredState() {
  STEP_BUILDER_STORAGE_KEYS.forEach(key => {
    try {
      window.localStorage.removeItem(key);
    } catch (error) {
      console.warn(`Unable to clear stored key: ${key}`, error);
    }
  });
}

/**
 * Reads the browser navigation type when available.
 *
 * @returns {string} Navigation type such as `navigate`, `reload`, or `back_forward`.
 */
function getNavigationEntryType() {
  try {
    return window.performance?.getEntriesByType?.('navigation')?.[0]?.type || '';
  } catch (error) {
    return '';
  }
}

/**
 * Determines whether the page load appears to be a return from checkout that should reset the builder.
 *
 * Expects the session reset flag to have been set before redirecting to checkout.
 *
 * @param {PageTransitionEvent|Event|undefined} event Page lifecycle event from `pageshow`.
 * @returns {boolean}
 */
function shouldResetStepBuilderAfterCheckout(event) {
  let hasResetFlag = false;
  try {
    hasResetFlag = window.sessionStorage.getItem(CHECKOUT_RETURN_RESET_KEY) === 'true';
  } catch (error) {
    hasResetFlag = false;
  }

  if (!hasResetFlag) return false;

  const referrer = document.referrer || '';
  const cameFromCheckout = /\/checkouts?(\/|$)|\/checkout(\/|$)/i.test(referrer);
  const isHistoryRestore = !!event?.persisted || getNavigationEntryType() === 'back_forward';

  return cameFromCheckout || isHistoryRestore;
}

/**
 * Clears persisted builder state and reloads the product page without a `variant` param after checkout return.
 *
 * @param {PageTransitionEvent|Event|undefined} event Page lifecycle event from `pageshow`.
 * @returns {void}
 */
function resetStepBuilderAfterCheckoutIfNeeded(event) {
  if (!shouldResetStepBuilderAfterCheckout(event)) return;

  try {
    window.sessionStorage.removeItem(CHECKOUT_RETURN_RESET_KEY);
  } catch (error) {
    console.warn('Unable to clear checkout reset flag.', error);
  }

  clearStepBuilderStoredState();

  const nextUrl = new URL(window.location.href);
  nextUrl.searchParams.delete('variant');
  window.location.replace(nextUrl.pathname + nextUrl.search + nextUrl.hash);
}

window.addEventListener('pageshow', resetStepBuilderAfterCheckoutIfNeeded);

/**
 * Capitalizes the first letter of each space-delimited word.
 *
 * @param {string} str Source text to transform.
 * @returns {string}
 */
function capitalize(str) {
  let words = str.split(" ");
  for (let i = 0; i < words.length; i++) {
    words[i] = words[i].charAt(0).toUpperCase() + words[i].slice(1); 
  }
  let capitalizedStr = words.join(" ");
  return capitalizedStr;
}

const AI_GENERATION_PRESETS = ['masculine', 'feminine'];
const AI_INLINE_LOADING_MESSAGES = [
  "We've seen over 1 million pet photos, and yours is our favourite",
  'Warning: Extreme cuteness loading...',
  'You must be a proud hooman!',
  'Almost ready now!',
  'Creating a pawsitively magnificent portrait'
];
const AI_STYLE_PRESET_RULES = {
  king: ['masculine'],
  queen: ['feminine'],
  princess: ['feminine'],
  military: ['masculine', 'feminine'],
  astronaut: ['masculine', 'feminine'],
  superhero: ['masculine', 'feminine'],
  'regal-bed': ['masculine', 'feminine']
};

/**
 * Upload wrapper custom element that waits for UploadKit controls to render and then
 * injects any product-specific companion inputs required by the current upload mode.
 *
 * Expects:
 * - `data-type` to describe the upload variant being rendered.
 * - UploadKit buttons to appear inside the element after third-party initialization.
 * - `#step-block-product` to contain serialized product JSON used to inspect tags.
 */
class BtnUpload extends HTMLElement {
  /**
   * Initializes the UploadKit bridge state and waits for third-party upload controls to render.
   */
  constructor() {
    super();

    this.uploadKitEv = new Event('adduploadkit');
    
    this.inputs = {};
    this.type = this.dataset.type;

    this.onWaitUploadKit();

    this.addEventListener('adduploadkit', () => {
      let nameInput = false;
      
      const product = JSON.parse(document.querySelector('#step-block-product').textContent);
      const nameTag = [
        'upload with custom text',
        'upload and text',
        'upload with pets name',
        'upload two pets with name',
        'upload three pets with name',
        'upload four pets with name',
        'upload five pets with name',
        'upload six pets with name',
        'upload with name and location',
      ];

      for (const tag of product.tags) {
        nameInput = nameTag.includes(tag);
        if(nameInput) break;
      }

      if(nameInput) {
        if (this.type === 'ages') {
          this.querySelectorAll('.uploadkit-button').forEach( (btn, arg) => {
            let proName = btn.dataset['uploaderName'];
      
            const label = document.createElement('label');
            let num = arg;
            if(document.querySelectorAll('.uploadkit-button').length > 1) {
              num = arg + 1;
            } else {
              num = '';
            }
    
            const labelName = document.createTextNode(`Age / Caption ${num}`);
            label.appendChild(labelName);
      
            const inputName = document.createElement('input');
            inputName.setAttribute('type', 'text');
            inputName.setAttribute('class', 'pet-name');
            inputName.setAttribute('name', `properties[_${proName}_age]`);
            inputName.setAttribute('placeholder', num === 1 ? 'e.g. Puppy / Kitten / Then / 8 Weeks Old' : 'e.g. Adult / Now / 4 Years Old');
            inputName.setAttribute('value', '');
      
            const pet_name = document.createElement('div');
            pet_name.classList.add('pet_name');
      
            pet_name.appendChild(label);
            pet_name.appendChild(inputName);
      
            btn.after(pet_name);
            let property = JSON.parse( `{"_${proName}\":"","_${proName} (direct link)":"","_${proName}_age":""}`);
            this.inputs.properties = {...this.inputs.properties, ...property};
          });
        } else if (this.type === 'additional text') {
          this.querySelectorAll('.uploadkit-button').forEach( (btn, arg) => {
      
            const label = document.createElement('label');

            let proName = arg === 0 ? 'left' : 'right';
    
            const labelName = document.createTextNode(`${arg === 0 ? 'Left photo name' : 'Right photo name'}`);
            label.appendChild(labelName);
      
            const inputName = document.createElement('input');
            inputName.setAttribute('type', 'text');
            inputName.setAttribute('class', 'pet-name');
            inputName.setAttribute('name', `properties[_${proName}_petName]`);
            inputName.setAttribute('placeholder', 'e.g Tom, Bruno, etc.');
            inputName.setAttribute('value', '');
      
            const pet_name = document.createElement('div');
            pet_name.classList.add('pet_name');
      
            pet_name.appendChild(label);
            pet_name.appendChild(inputName);
      
            btn.after(pet_name);
            let property = JSON.parse( `{"_${proName}\":"","_${proName} (direct link)":"","_${proName}_petName":""}`);
            this.inputs.properties = {...this.inputs.properties, ...property};
          });
        } else {
          this.querySelectorAll('.uploadkit-button').forEach( (btn, arg) => {
            let proName = btn.dataset['uploaderName'];
      
            const label = document.createElement('label');
            let num = arg;
            if(document.querySelectorAll('.uploadkit-button').length > 1) {
              num = arg + 1;
            } else {
              num = '';
            }
    
            const labelName = document.createTextNode(`Pet Name ${num}`);
            label.appendChild(labelName);
      
            const inputName = document.createElement('input');
            inputName.setAttribute('type', 'text');
            inputName.setAttribute('class', 'pet-name');
            inputName.setAttribute('name', `properties[_${proName}_petName]`);
            inputName.setAttribute('placeholder', 'e.g Tom, Bruno, etc.');
            inputName.setAttribute('value', '');
      
            const pet_name = document.createElement('div');
            pet_name.classList.add('pet_name');
      
            pet_name.appendChild(label);
            pet_name.appendChild(inputName);
      
            btn.after(pet_name);
            let property = JSON.parse( `{"_${proName}\":"","_${proName} (direct link)":"","_${proName}_petName":""}`);
            this.inputs.properties = {...this.inputs.properties, ...property};
          });
        }
        if (document.querySelector('#additional-text')) {
          this.inputs.properties = {
            ...this.inputs.properties,
            _additional_text: ""
          };
        }
        if (document.querySelector('#pet-name')) {
          this.inputs.properties = {
            ...this.inputs.properties,
            _pet_name: ""
          };
        }
      }
    });
  }

  /**
   * Polls until UploadKit has rendered at least one upload button, then triggers input injection.
   *
   * @returns {void}
   */
  onWaitUploadKit() {
    let uploadkit = setInterval(() => {
      if(this.querySelector('.uploadkit-button')) {
        this.dispatchEvent(this.uploadKitEv);
        clearInterval(uploadkit);
      }
    }, 500);
  }

}
customElements.define('upload-btn-wrapper', BtnUpload);

/**
 * Back-button custom element that translates clicks into a bubbling `before` event for
 * the parent step controller.
 */
class BtnBefore extends HTMLElement {
  /**
   * Creates the bubbling navigation event used by the step controller.
   */
  constructor() {
    super();

    this.beforeEv = new Event('before', {bubbles: true});

    this.addEventListener('click', this.onBeforeClick.bind(this));
  }

  /**
   * Prevents native button behavior and emits the semantic previous-step event.
   *
   * @param {MouseEvent} event
   * @returns {void}
   */
  onBeforeClick(event) {
    event.preventDefault();
    // debugger;
    this.dispatchEvent(this.beforeEv);
  }
}
customElements.define('before-btn', BtnBefore);

/**
 * Forward-button custom element that translates clicks into a bubbling `next` event for
 * the parent step controller.
 */
class BtnNext extends HTMLElement {
  /**
   * Creates the bubbling navigation event used by the step controller.
   */
  constructor() {
    super();

    this.nextEv = new Event('next', {bubbles: true});

    this.addEventListener('click', this.onNextClick.bind(this));
  }

  /**
   * Prevents native button behavior and emits the semantic next-step event.
   *
   * @param {MouseEvent} event
   * @returns {void}
   */
  onNextClick(event) {
    event.preventDefault();
    // debugger;
    this.dispatchEvent(this.nextEv);
  }
}
customElements.define('next-btn', BtnNext);

/**
 * Selectable option chip/card for a single step value.
 *
 * Expects:
 * - `data-v_option` to contain the option value represented by the element.
 * - `data-option` to identify the step name so sibling options can be discovered.
 * - To live inside a step container that groups related `option-item` elements.
 */
class OptionItem extends HTMLElement {
  /**
   * Registers the option-selection event emitted when the card is chosen.
   */
  constructor() {
    super();

    this.selectEv = new Event('selectopt', {bubbles: true});

    this.addEventListener('click', this.onSelectOption.bind(this));
  }

  /**
   * Hydrates the option metadata and caches sibling option elements for selection toggling.
   *
   * Expects `data-v_option` and `data-option` to be present on the element.
   *
   * @returns {void}
   */
  connectedCallback() {
    this.v_option = this.dataset['v_option'];
    this.step = this.dataset['option'];
    let parentEl = document.querySelector(`[data-step="${this.step}"]`) || this.parentElement;
    try {
      const siblings = Array.from(parentEl.querySelectorAll('option-item'))
        .filter(sibling => sibling !== this);
      this.siblings = siblings.length ? siblings : null;
    } catch (err) {
      console.error(`Error selecting siblings: ${err.message}`);
      this.siblings = null;
    }
  }

  /**
   * Marks the clicked option as selected, clears sibling selections, and emits `selectopt`.
   *
   * @returns {void}
   */
  onSelectOption() {
    if (!this.siblings) return;
    this.siblings.forEach(sibling => sibling.classList.toggle('selected', false));

    this.classList.toggle('selected', true);
    setTimeout(() => {
      this.dispatchEvent(this.selectEv);
    }, 300);
  }
}
customElements.define('option-item', OptionItem);

/**
 * Toggle button used for optional upsells/extras in the step flow.
 *
 * Expects `data-v_id` to reference the variant id for the extra item so the parent
 * controller can add or remove it from the cart payload.
 */
class ExtraAdd extends HTMLElement {
  /**
   * Initializes the extra-item toggle state and the add/remove events consumed by `StepBlock`.
   */
  constructor() {
    super();
    this.v_id = this.dataset['v_id'];

    this.addExtraEv = new Event('addextra', {bubbles: true});
    this.removeExtraEv = new Event('removeextra', {bubbles: true});

    this.addEventListener('click', this.updateState.bind(this));
  }

  /**
   * Toggles the extra item between selected and unselected states.
   *
   * @param {MouseEvent} ev
   * @returns {void}
   */
  updateState(ev) {
    ev.preventDefault();

    if(this.classList.contains('added')) {
      this.classList.remove('added');
      this.dispatchEvent(this.removeExtraEv);
    } else {
      this.classList.add('added');
      this.dispatchEvent(this.addExtraEv);
    }
  }
}
customElements.define('extra-add', ExtraAdd); 

/**
 * Primary orchestration element for the AI product builder.
 *
 * Responsibilities:
 * - Hydrate product and metafield data from server-rendered JSON.
 * - Resolve the active Shopify variant and synchronize selected option UI.
 * - Determine which steps are currently visible and which action buttons are enabled.
 * - Manage AI generation inputs, modal/inline preview state, and generated asset metadata.
 * - Assemble cart line items, sanitize custom properties, and send the final add-to-cart request.
 *
 * Expects a fully rendered product step-builder DOM, including serialized product JSON,
 * navigation buttons, upload controls, and any optional AI-preview modal markup.
 */
class StepBlock extends HTMLElement {
  /**
   * Hydrates the product builder, initializes variant and preview state, and binds all UI actions.
   *
   * Expects the surrounding product page to provide the serialized product JSON, upload controls,
   * navigation buttons, and optional AI-preview markup referenced throughout the builder.
   */
  constructor() {
    super();

    if (!StepBlock._instance) {
      StepBlock._instance = this;
    }
    
    this.steps = [];
    this.hasExplicitSizeSelection = false;
    this.url = new URL(window.location.href);
    this.cur_symbol = document.querySelector('#currency_symbol').textContent;
    this.product = JSON.parse(document.querySelector('#step-block-product').textContent);
    this.variantSteps = this.product.options
      .map(option => option.toLowerCase())
      .filter(step => this.isRenderableStep(step));
    if (this.getGenerationStepElement() && !this.variantSteps.some(step => this.getStepSelector(step) === '#step-style')) {
      this.variantSteps.unshift('style');
    }
    
    this.steps = [...this.variantSteps];
    let m_product_containers = document.querySelectorAll('.step-block-product_metafields');
    this.m_products = [];
    for (const element of m_product_containers) {
      let m_product = JSON.parse(element.textContent);
      this.m_products.push(m_product);
    }
    
    localStorage.setItem('custom_product', this.product.handle);
    const extraTitle = document.querySelector('#step-extras .process-name')?.textContent || '';
    localStorage.setItem('extra_title', extraTitle);

    localStorage.setItem('m_products', JSON.stringify(this.m_products));

    let curVariantID = this.url.searchParams.get("variant");

    this.cur_variant = this.getInitialVariant(curVariantID);

    if (this.cur_variant) {
      this.cur_options = this.cur_variant.options.join();
      let optionNames = this.product.options;
      this.cur_variant.options.forEach( (option, arg) => {
        document.querySelectorAll(`[data-step="${optionNames[arg]}"] option-item`).forEach( item => {
          let v_option = item.dataset['v_option'];
          v_option == option ? item.classList.toggle('selected', true) : null;
        })
      });
      this.dataset['v_id'] = this.cur_variant.id;
      
      this.url.searchParams.set("variant", this.cur_variant.id);
      window.history.replaceState({}, "", this.url);
    }

    this.allSteps = ['upload', ...this.variantSteps];
    this.refreshStepFlow();

    this.items = [
      {
        id: this.cur_variant.id,
        quantity: 1,
        price: this.cur_variant.price,
        properties: {
          '__step-product': true
        }
      }
    ];
    
    this.addCartEv = new Event('customaddcart', {bubbles: true});

    this.beforeBtn = this.querySelector('before-btn');
    this.nextBtn = this.querySelector('next-btn');
    this.addCartBtn = this.querySelector('.atc-btn');
    this.uploadWrap = this.querySelector('upload-btn-wrapper');
    this.generatedPreviewState = {
      loading: false,
      imageUrl: '',
      previewUrl: '',
      style: '',
      prompt: '',
      errorMessage: ''
    };
    this.selectedGenerationPreset = '';
    this.promptPlaceholderInterval = null;
    this.promptPlaceholderIndex = 0;
    this.inlineLoadingMessageInterval = null;
    this.inlineLoadingMessageIndex = 0;
    this.sampleImagesInterval = null;
    this.uploadChangeWatchInterval = null;
    this.lastKnownUploadedImageUrl = '';
    this.sampleImagesContainer = document.querySelector('.sample-ai-generated-images');
    
    this.selectedGenerationStyle = '';
    this.initializeGenerationStyleSelection();
    
    this.setStep(0, false);

    this.addActions();
    this.ensureGenerateButton();
    this.syncGenerationPresetAvailability(this.selectedGenerationStyle);
    this.updateGenerateButtonState();
    this.syncPreviewAvailabilityState();
    // this.startSampleImagesRotation();
    this.startUploadChangeWatcher();

    this.updateSubTotal();

  }

  /**
   * Resolves the starting variant from the URL, then falls back to the first available variant.
   *
   * @param {string|number|null} curVariantID Variant id from the URL query string.
   * @returns {Object|null}
   */
  getInitialVariant(curVariantID) {
    return this.product.variants.find(variant => variant.id == curVariantID)
      || this.product.variants.find(variant => variant.available)
      || this.product.variants[0]
      || null;
  }

  /**
   * Finds the product option index that represents size/format, including digital-download fallbacks.
   *
   * @returns {number}
   */
  getSizeOptionIndex() {
    const namedSizeOptionIndex = this.product.options.findIndex(option => /(size|format)/i.test(option));
    if (namedSizeOptionIndex > -1) return namedSizeOptionIndex;

    return this.product.options.findIndex((option, index) => {
      return this.product.variants.some(variant => {
        return (variant?.options?.[index] || '').trim().toLowerCase() == 'digital download';
      });
    });
  }

  /**
   * Returns the size/format value for the supplied variant.
   *
   * @param {Object|null} [variant=this.cur_variant]
   * @returns {string}
   */
  getSizeOptionValue(variant=this.cur_variant) {
    const sizeOptionIndex = this.getSizeOptionIndex();
    if (sizeOptionIndex === -1) return '';

    return (variant?.options?.[sizeOptionIndex] || '').trim();
  }

  /**
   * Checks whether a step name represents the size/format step.
   *
   * @param {string} [stepName='']
   * @returns {boolean}
   */
  isFormatStep(stepName='') {
    return /^(size|format)$/i.test((stepName || '').trim());
  }

  /**
   * Determines whether a variant represents the digital-download format.
   *
   * @param {Object|null} [variant=this.cur_variant]
   * @returns {boolean}
   */
  isDigitalDownloadVariant(variant=this.cur_variant) {
    return this.getSizeOptionValue(variant).toLowerCase() == 'digital download';
  }

  /**
   * Convenience wrapper around `isDigitalDownloadVariant` for the current variant.
   *
   * @returns {boolean}
   */
  isDigitalDownload() {
    return this.isDigitalDownloadVariant(this.cur_variant);
  }

  /**
   * Checks whether a step token refers to the framing step.
   *
   * @param {string} step
   * @returns {boolean}
   */
  isFrameStep(step) {
    return step == 'frame' || step == 'framing option';
  }

  /**
   * Determines whether a logical step has a matching DOM selector in the builder.
   *
   * @param {string} step
   * @returns {boolean}
   */
  isRenderableStep(step) {
    return !!this.getStepSelector(step);
  }

  /**
   * Maps a logical option/step name to the DOM selector used by the builder process UI.
   *
   * @param {string} step
   * @returns {string|null}
   */
  getStepSelector(step) {
    const selectorMap = {
      upload: '#step-upload',
      color: '#step-background',
      'tray color': '#step-background',
      theme: '#step-style',
      style: '#step-style',
      // size: '#step-size',
      format: '#step-size',
      // frame: '#step-frame',
      // 'framing option': '#step-frame'
    };

    return selectorMap[step] || null;
  }

  /**
   * Returns the ordered list of steps that should be visible for the supplied variant.
   *
   * @param {Object|null} [variant=this.cur_variant]
   * @returns {string[]}
   */
  getStepsForVariant(variant=this.cur_variant) {
    const shouldShowFrameStep = this.shouldShowFrameStep(variant);
    return this.allSteps.filter(step => !this.isFrameStep(step) || shouldShowFrameStep);
  }

  /**
   * Recomputes the active sequence of visible steps for the supplied variant and updates
   * any conditional UI that depends on that sequence, such as the frame/framing step.
   *
   * @param {Object|null} [variant=this.cur_variant] Shopify variant whose option values
   * determine whether conditional steps should appear.
   * @returns {void}
   */
  refreshStepFlow(variant=this.cur_variant) {
    this.steps = this.getStepsForVariant(variant);
    this.toggleFrameStepVisibility(variant);
  }

  /**
   * Determines whether the framing step should appear for the supplied variant.
   *
   * Expects size selection to be explicit before framing is shown when digital download is possible.
   *
   * @param {Object|null} [variant=this.cur_variant]
   * @returns {boolean}
   */
  shouldShowFrameStep(variant=this.cur_variant) {
    const hasFrameStep = this.allSteps.some(step => this.isFrameStep(step));
    if (!hasFrameStep) return false;

    const sizeOptionIndex = this.getSizeOptionIndex();
    if (sizeOptionIndex === -1) return true;
    if (!this.hasExplicitSizeSelection) return false;

    return !this.isDigitalDownloadVariant(variant);
  }

  /**
   * Shows or hides the frame process UI to match the currently valid step flow.
   *
   * @param {Object|null} [variant=this.cur_variant]
   * @returns {void}
   */
  toggleFrameStepVisibility(variant=this.cur_variant) {
    const shouldHideFrame = !this.shouldShowFrameStep(variant);
    const frameProcess = this.querySelector('#step-frame') || document.querySelector('#step-frame');
    if (frameProcess) {
      frameProcess.toggleAttribute('hidden', shouldHideFrame);
    }

    const frameStep = this.querySelector('.frame-step') || document.querySelector('.frame-step');
    if (frameStep) {
      frameStep.toggleAttribute('hidden', shouldHideFrame);
    }
  }

  /**
   * Collects the rendered process elements for the currently active steps.
   *
   * @returns {HTMLElement[]}
   */
  getVisibleStepProcesses() {
    return this.steps
      .map(step => {
        const selector = this.getStepSelector(step);
        return selector ? (this.querySelector(selector) || document.querySelector(selector)) : null;
      })
      .filter(Boolean);
  }

  /**
   * Returns the option index for a product step name.
   *
   * @param {string} step
   * @returns {number}
   */
  getVariantStepIndex(step) {
    return this.product.options.findIndex(option => option.toLowerCase() == step);
  }

  /**
   * Resolves the product option index used for AI style selection.
   *
   * @returns {number}
   */
  getGenerationOptionIndex() {
    const preferredOptionIndex = this.product.options.findIndex(option => /(style|theme)/i.test(option));
    if (preferredOptionIndex > -1) return preferredOptionIndex;

    return -1;
  }

  /**
   * Returns the logical step name used for the AI generation style selector.
   *
   * @returns {string}
   */
  getGenerationStepName() {
    const stepElement = this.getGenerationStepElement();
    return stepElement?.dataset?.step || this.product.options[this.getGenerationOptionIndex()] || '';
  }

  /**
   * Locates the DOM element for the AI generation step.
   *
   * @returns {HTMLElement|null}
   */
  getGenerationStepElement() {
    return this.querySelector('[data-generation-step="true"]') || document.querySelector('[data-generation-step="true"]');
  }

  /**
   * Hydrates the initial AI style selection from the current variant and selected option UI.
   *
   * @returns {void}
   */
  initializeGenerationStyleSelection() {
    const stepElement = this.getGenerationStepElement();
    if (!stepElement) return;

    let selectedOption = stepElement.querySelector('option-item.selected');
    if (!selectedOption) {
      selectedOption = stepElement.querySelector('option-item');
      selectedOption?.classList.add('selected');
    }

    const generationOptionIndex = this.getGenerationOptionIndex();
    const variantStyleValue = generationOptionIndex > -1
      ? (this.cur_variant?.options?.[generationOptionIndex] || '')
      : '';
    const selectedStyleValue = selectedOption?.dataset?.v_option || '';
    let initialStyleValue = variantStyleValue || selectedStyleValue;

    if (initialStyleValue && selectedOption && selectedStyleValue !== initialStyleValue) {
      stepElement.querySelectorAll('option-item').forEach(option => {
        option.classList.toggle('selected', option.dataset.v_option == initialStyleValue);
      });
      selectedOption = stepElement.querySelector('option-item.selected') || selectedOption;
      initialStyleValue = selectedOption?.dataset?.v_option || selectedStyleValue;
    }

    this.selectedGenerationStyle = initialStyleValue;
  }

  /**
   * Normalizes a style value for case-insensitive comparisons.
   *
   * @param {string} [value='']
   * @returns {string}
   */
  normalizeGenerationValue(value='') {
    return (value || '').trim().toLowerCase();
  }

  /**
   * Returns the prompt presets allowed for the current generation style.
   *
   * @param {string} [styleValue=this.selectedGenerationStyle]
   * @returns {string[]}
   */
  getAllowedGenerationPresets(styleValue=this.selectedGenerationStyle) {
    if (typeof HIDE_GENDER_OPTIONS !== 'undefined' && HIDE_GENDER_OPTIONS) return [];

    const normalizedStyle = this.normalizeGenerationValue(styleValue);
    return AI_STYLE_PRESET_RULES[normalizedStyle] || [...AI_GENERATION_PRESETS];
  }

  /**
   * Checks whether a specific prompt preset can be used with the supplied style.
   *
   * @param {string} preset
   * @param {string} [styleValue=this.selectedGenerationStyle]
   * @returns {boolean}
   */
  isGenerationPresetAllowed(preset, styleValue=this.selectedGenerationStyle) {
    return this.getAllowedGenerationPresets(styleValue).includes(this.normalizeGenerationValue(preset));
  }

  /**
   * Removes preset helper text from a prompt so only user-authored content remains.
   *
   * @param {string} [promptValue='']
   * @returns {string}
   */
  stripGenerationPresetText(promptValue='') {
    let nextPrompt = (promptValue || '').trim();

    AI_GENERATION_PRESETS
      .map(preset => this.getPromptPresetText(preset))
      .filter(Boolean)
      .forEach(text => {
        nextPrompt = nextPrompt.replace(text, '').trim();
      });

    return nextPrompt.replace(/\n{3,}/g, '\n\n').trim();
  }

  /**
   * Applies enabled/disabled state to preset UI controls based on the selected generation style.
   *
   * @param {string} [styleValue=this.selectedGenerationStyle]
   * @returns {void}
   */
  syncGenerationPresetAvailability(styleValue=this.selectedGenerationStyle) {
    const allowedPresets = this.getAllowedGenerationPresets(styleValue);
    const nextPreset = allowedPresets.length === 1 ? allowedPresets[0] : '';
    const nextPrompt = this.stripGenerationPresetText(this.generatedPreviewState.prompt || '');

    this.selectedGenerationStyle = styleValue || '';
    this.selectedGenerationPreset = nextPreset;
    this.generatedPreviewState.style = this.selectedGenerationStyle;
    this.generatedPreviewState.prompt = nextPrompt;

    const { prompt } = this.ensureGeneratedPreviewElements();
    if (prompt) {
      prompt.value = nextPrompt;
    }

    this.syncPromptPresetSelection(nextPrompt);
  }

  /**
   * Creates or reuses the AI generation CTA within the current step UI and wires its click handler.
   *
   * @returns {HTMLButtonElement|null}
   */
  ensureGenerateButton() {
    const stepElement = this.getGenerationStepElement();
    if (!stepElement) return null;

    let actionWrap = stepElement.querySelector('.ai-generate-action');
    if (!actionWrap) {
      actionWrap = document.createElement('div');
      actionWrap.className = 'ai-generate-action';
      actionWrap.style.willChange = 'transform';

      const hideGenderOptions = typeof HIDE_GENDER_OPTIONS !== 'undefined' && HIDE_GENDER_OPTIONS;

      if (!hideGenderOptions) {
        const presetWrap = document.createElement('div');
        presetWrap.className = 'ai-generate-presets';

        AI_GENERATION_PRESETS.forEach(preset => {
          const presetButton = document.createElement('button');
          presetButton.type = 'button';
          presetButton.className = 'ai-generate-preset-button';
          presetButton.dataset.aiGeneratePreset = preset;
          presetButton.textContent = capitalize(preset);
          presetButton.addEventListener('click', () => {
            this.setGenerationPreset(preset);
          });
          presetWrap.appendChild(presetButton);
        });

        const helper = document.createElement('div');
        helper.className = 'ai-generate-helper';
        helper.style.cssText = 'margin:0 0 16px;text-align:center;color:rgba(255,255,255,.68);font:600 14px/1.45 Assistant,sans-serif;';
        helper.textContent = 'Choose the portrait tone first, then generate your preview.';

        actionWrap.appendChild(presetWrap);
        actionWrap.appendChild(helper);
      }

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'ai-generate-button';
      button.textContent = 'Create a masterpiece with AI';
      button.addEventListener('click', () => {
        this.goToNextStepFromGeneration();
        this.generatePreviewForStyle(this.selectedGenerationStyle, this.getSelectedGenerationOptionElement());
      });

      actionWrap.appendChild(button);
      stepElement.appendChild(actionWrap);
    }

    this.updateGeneratePresetButtons();
    return actionWrap.querySelector('.ai-generate-button');
  }

  /**
   * Advances the builder after a successful generation when the next step is available.
   *
   * @returns {void}
   */
  goToNextStepFromGeneration() {
    const currentIndex = parseInt(this.dataset['index'], 10);
    if (Number.isNaN(currentIndex) || currentIndex >= this.steps.length - 1) return;

    this.setStep(currentIndex + 1);
  }

  /**
   * Scrolls the current set of visible step process cards into view.
   *
   * @returns {void}
   */
  scrollToStepProcesses() {
    const stepProcesses = document.querySelector('.step-processes');
    if (!stepProcesses) return;

    const headerHeight = document.querySelector('.headernav')?.offsetHeight || 0;
    const targetTop = window.scrollY + stepProcesses.getBoundingClientRect().top - headerHeight;

    window.scrollTo({
      top: Math.max(targetTop, 0),
      behavior: 'smooth'
    });
  }

  /**
   * Triggers the temporary attention state used when generation cannot proceed yet.
   *
   * @returns {void}
   */
  triggerGenerateActionShake() {
    const stepElement = this.getGenerationStepElement();
    const actionWrap = stepElement?.querySelector('.ai-generate-action');
    if (!actionWrap) return;

    actionWrap.animate([
      { transform: 'translateX(0)' },
      { transform: 'translateX(-8px)' },
      { transform: 'translateX(8px)' },
      { transform: 'translateX(-6px)' },
      { transform: 'translateX(6px)' },
      { transform: 'translateX(-3px)' },
      { transform: 'translateX(3px)' },
      { transform: 'translateX(0)' }
    ], {
      duration: 420,
      easing: 'ease-out'
    });
  }

  /**
   * Returns the currently selected style option element for the generation step.
   *
   * @returns {HTMLElement|null}
   */
  getSelectedGenerationOptionElement() {
    const stepElement = this.getGenerationStepElement();
    return stepElement?.querySelector('option-item.selected') || null;
  }

  /**
   * Updates the generate CTA label, disabled state, and affordance hints from current builder state.
   *
   * @returns {void}
   */
  updateGenerateButtonState() {
    const button = this.ensureGenerateButton();
    if (!button) return;
    const helper = this.getGenerationStepElement()?.querySelector('.ai-generate-helper');
    const allowedPresets = this.getAllowedGenerationPresets();
    const hasSinglePreset = allowedPresets.length === 1;
    const hideGenderOptions = typeof HIDE_GENDER_OPTIONS !== 'undefined' && HIDE_GENDER_OPTIONS;

    const canGenerate = !!this.selectedGenerationStyle
      && !!this.getUploadedImageUrl()
      && (hideGenderOptions || !!this.selectedGenerationPreset)
      && !this.generatedPreviewState.loading;
    button.disabled = !canGenerate;
    button.classList.toggle('is-disabled', !canGenerate);
    button.classList.toggle('is-loading', !!this.generatedPreviewState.loading);
    button.textContent = this.generatedPreviewState.loading
      ? 'Generating...'
      : hideGenderOptions || this.selectedGenerationPreset
        ? 'Create a masterpiece with AI'
        : 'Choose masculine or feminine first';
    if (helper) {
      helper.textContent = hasSinglePreset
        ? `${capitalize(allowedPresets[0])} is automatically selected for this style.`
        : this.selectedGenerationPreset
          ? `Portrait tone selected: ${capitalize(this.selectedGenerationPreset)}. You can still re-edit it later.`
          : 'Choose the portrait tone first, then generate your preview.';
    }
    this.updateGeneratePresetButtons();
  }

  /**
   * Infers which preset is active by inspecting the current prompt text.
   *
   * @param {string} [promptValue='']
   * @returns {string}
   */
  inferGenerationPresetFromPrompt(promptValue='') {
    const normalizedPrompt = promptValue.toLowerCase();
    const presetOrder = ['masculine', 'feminine'];

    return presetOrder.find(preset => {
      const presetText = this.getPromptPresetText(preset).toLowerCase();
      return presetText && normalizedPrompt.includes(presetText);
    }) || '';
  }

  /**
   * Synchronizes the preset button selected state with `selectedGenerationPreset`.
   *
   * @returns {void}
   */
  updateGeneratePresetButtons() {
    const stepElement = this.getGenerationStepElement();
    if (!stepElement) return;
    const allowedPresets = this.getAllowedGenerationPresets();

    stepElement.querySelectorAll('[data-ai-generate-preset]').forEach(button => {
      const isAllowed = allowedPresets.includes(button.dataset.aiGeneratePreset);
      const isSelected = button.dataset.aiGeneratePreset === this.selectedGenerationPreset;
      button.disabled = !isAllowed;
      button.setAttribute('aria-disabled', isAllowed ? 'false' : 'true');
      button.classList.toggle('is-selected', isSelected);
      button.classList.toggle('is-disabled', !isAllowed);
    });

    const { presetButtons } = this.ensureGeneratedPreviewElements();
    presetButtons?.forEach(button => {
      const isAllowed = allowedPresets.includes(button.dataset.aiGeneratedPreset);
      button.disabled = !isAllowed;
      button.setAttribute('aria-disabled', isAllowed ? 'false' : 'true');
      button.classList.toggle('is-selected', button.dataset.aiGeneratedPreset === this.selectedGenerationPreset);
      button.classList.toggle('is-disabled', !isAllowed);
    });
  }

  /**
   * Reads the current uploaded pet image URL from line-item properties or the upload UI.
   *
   * @returns {string}
   */
  getUploadedImageUrl() {
    const uploadedInput = this.uploadWrap?.querySelector('input[name*="(direct link)"]');
    if (uploadedInput?.value) return uploadedInput.value;

    const properties = this.items?.[0]?.properties || {};
    const uploadedDirectKey = Object.keys(properties).find(key => key.includes('(direct link)') && properties[key]);

    if (uploadedDirectKey) return properties[uploadedDirectKey];
    return '';
  }

  /**
   * Copies live upload-related form input values back into the main cart item property bag.
   *
   * @returns {void}
   */
  syncUploadPropertiesFromInputs() {
    if (!this.items?.[0]) return;

    if (!this.items[0].properties) {
      this.items[0].properties = {};
    }

    Object.assign(this.items[0].properties, { ...(this.uploadWrap?.inputs?.properties || {}) });

    this.uploadWrap?.querySelectorAll('input[name^="properties["]').forEach(input => {
      const key = input.name.replace('properties[', '').replace(']', '');
      this.items[0].properties[key] = input.value;
    });
  }

  /**
   * Ensures the preview modal has a proxy control that reopens the original upload picker.
   *
   * @param {HTMLElement} modal AI preview modal element.
   * @returns {{section?: HTMLElement, uploadReplace?: HTMLElement, proxyButton?: HTMLButtonElement}}
   */
  ensureModalImageReplaceSection(modal) {
    if (!modal || !this.uploadWrap) return {};
    
    let section = modal.querySelector('.ai-change-image');

    const uploadReplace = section.querySelector('.ai-upload-replace');
    if (!uploadReplace) return { section };

    const sourceButton = this.uploadWrap.querySelector('.uploadkit-button');
    if (!sourceButton) return { section, uploadReplace };

    if (!uploadReplace.querySelector('[data-ai-upload-replace-proxy]')) {
      const proxyButton = document.createElement('button');
      proxyButton.type = 'button';
      proxyButton.className = sourceButton.className;
      proxyButton.dataset.aiUploadReplaceProxy = 'true';
      proxyButton.textContent = sourceButton.dataset.replaceLabel || 'Change Image';
      proxyButton.addEventListener('click', event => {
        event.preventDefault();
        sourceButton.click();
        this.openUploadKitComputerSource();
      });
      uploadReplace.appendChild(proxyButton);
    }

    return {
      section,
      uploadReplace,
      proxyButton: uploadReplace.querySelector('[data-ai-upload-replace-proxy]')
    };
  }

  /**
   * Attempts to open UploadKit directly to the "My computer" source after its chooser appears.
   *
   * @returns {void}
   */
  openUploadKitComputerSource() {
    let attempts = 0;
    const maxAttempts = 20;
    const openComputerPicker = window.setInterval(() => {
      attempts += 1;

      const computerSource = document.querySelector('[title="My computer"]');
      if (computerSource) {
        window.clearInterval(openComputerPicker);
        computerSource.click();
        return;
      }

      if (attempts >= maxAttempts) {
        window.clearInterval(openComputerPicker);
      }
    }, 200);
  }

  /**
   * Extracts the raw URL from a CSS `background-image` value.
   *
   * @param {string} cssValue
   * @returns {string}
   */
  extractUrlFromCssValue(cssValue) {
    const match = cssValue?.match(/url\((['"]?)(.*?)\1\)/);
    return match?.[2] || '';
  }

  /**
   * Resolves the preview image URL associated with a style option card.
   *
   * @param {HTMLElement|null} optionElement
   * @returns {string}
   */
  getStylePreviewImageUrl(optionElement) {
    if (!optionElement) return '';

    const previewImage = optionElement.querySelector('img');
    if (previewImage?.src) return previewImage.src;

    const styleTarget = optionElement.querySelector('.bg-wrapper, .frame-wrapper, .size-wrapper');
    if (!styleTarget) return '';

    const inlineUrl = this.extractUrlFromCssValue(styleTarget.style.backgroundImage);
    if (inlineUrl) return inlineUrl;

    const computedUrl = this.extractUrlFromCssValue(window.getComputedStyle(styleTarget).backgroundImage);
    return computedUrl || '';
  }

  /**
   * Builds the payload sent to the AI generation endpoint from the current builder state.
   *
   * The payload merges the selected style, uploaded source image, optional style-preview
   * thumbnail, and the normalized/customized prompt text currently shown in the preview UI.
   *
   * @param {string} styleValue Selected generation style value.
   * @param {HTMLElement|null} [optionElement=null] Style option element used to resolve a
   * preview thumbnail when available.
   * @returns {{
   *   productId: number|string,
   *   variantId: number|string|undefined,
   *   style: string,
   *   sourceImageUrl: string,
   *   stylePreviewUrl: string,
   *   customPrompt: string
   * }}
   */
  getGenerationRequestPayload(styleValue, optionElement=null) {
    const visiblePrompt = this.stripGenerationPresetText(this.generatedPreviewState.prompt || '');
    const presetPrompt = this.getPromptPresetText(this.selectedGenerationPreset).trim();
    const customPrompt = [presetPrompt, visiblePrompt].filter(Boolean).join('\n').trim();

    return {
      productId: this.product.id,
      variantId: this.cur_variant?.id,
      style: styleValue,
      sourceImageUrl: this.getUploadedImageUrl(),
      stylePreviewUrl: this.getStylePreviewImageUrl(optionElement),
      customPrompt
    };
  }

  /**
   * Picks a fallback/demo image when live generation is unavailable.
   *
   * @param {string} styleValue
   * @param {HTMLElement|null} [optionElement=null]
   * @returns {string}
   */
  getDemoGeneratedImageUrl(styleValue, optionElement=null) {
    const stylePreviewUrl = this.getStylePreviewImageUrl(optionElement);
    if (stylePreviewUrl) return stylePreviewUrl;

    const mediaUrls = (this.product.media || [])
      .map(media => media?.src || media?.preview_image?.src || media?.featured_image?.src)
      .filter(Boolean);

    if (mediaUrls.length) {
      const styleSeed = Array.from(styleValue || '').reduce((sum, char) => sum + char.charCodeAt(0), 0);
      return mediaUrls[styleSeed % mediaUrls.length];
    }

    return this.getUploadedImageUrl();
  }

  /**
   * Simulates a successful generation response for demo or fallback scenarios.
   *
   * @param {Object} payload Base generation payload.
   * @returns {Promise<Object>}
   */
  requestDemoGeneratedPreview(payload) {
    return new Promise(resolve => {
      window.setTimeout(() => {
        resolve({
          ...payload,
          demo: true,
          imageUrl: payload.stylePreviewUrl || this.getDemoGeneratedImageUrl(payload.style)
        });
      }, 900);
    });
  }

  /**
   * Re-uploads the chosen source image so the generation service receives a stable file URL.
   *
   * @param {string} imageUrl Public image URL to fetch and re-upload.
   * @param {string} [imageFileName='pet-image.jpg'] Filename sent with the upload request.
   * @returns {Promise<string>}
   */
  async uploadImageToServer(imageUrl, imageFileName = 'pet-image.jpg') {
    try {
      const uploadUrl = API_CONFIG.getUploadUrl();
      const formData = new FormData();
      
      // Fetch the image from URL and convert it to a blob
      const imageResponse = await fetch(imageUrl);
      if (!imageResponse.ok) {
        throw new Error(`Failed to fetch image: ${imageResponse.statusText}`);
      }
      
      const imageBlob = await imageResponse.blob();
      formData.append('image', imageBlob, imageFileName);

      const uploadResponse = await fetch(uploadUrl, {
        method: 'POST',
        body: formData
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed with status ${uploadResponse.status}`);
      }

      const uploadResult = await uploadResponse.json();
      
      // Return the uploaded file URL from the response
      return uploadResult.imageUrl || uploadResult.fileUrl || uploadResult.uploadedUrl || imageUrl;
    } catch (error) {
      console.error('Image upload failed:', error);
      throw error;
    }
  }

  /**
   * Requests an AI-generated preview for the currently uploaded pet image and selected style.
   *
   * Flow:
   * - Creates the base payload from current UI state.
   * - Attempts to upload the source image first so downstream services receive a durable URL.
   * - Sends the normalized request body to the generation API configured on the element/global config.
   * - Maps several possible API response shapes into the preview object expected by the builder.
   *
   * @param {string} styleValue Selected generation style value.
   * @param {HTMLElement|null} [optionElement=null] Style option element used for preview metadata.
   * @returns {Promise<{
   *   success: boolean,
   *   previewUrl: string,
   *   imageUrl: string,
   *   sourceImageUrl: string,
   *   style: string,
   *   prompt: string,
   *   jobId: string|number|undefined,
   *   originalImageUrl: string,
   *   generatedAt: string,
   *   fullResponse: Object
   * }>}
   */
  async requestGeneratedPreview(styleValue, optionElement=null) {
    const payload = this.getGenerationRequestPayload(styleValue, optionElement);
    const apiUrl = this.dataset.aiGenerateUrl || window.__aiImageGenerateApiUrl || API_CONFIG.getGenerateUrl();

    try {
      // First, try to upload the source image
      let uploadedImageUrl = payload.sourceImageUrl;
      if (payload.sourceImageUrl) {
        try {
          uploadedImageUrl = await this.uploadImageToServer(payload.sourceImageUrl, `pet-session-${Date.now()}.jpg`);
        } catch (uploadError) {
          console.warn('Upload failed, proceeding with original URL:', uploadError);
          // Continue with original URL if upload fails
        }
      }

      // Prepare the generation request payload
      const generationPayload = {
        productId: payload.productId,
        variantId: payload.variantId,
        style: payload.style,
        imageUrl: uploadedImageUrl,
        stylePreviewUrl: payload.stylePreviewUrl,
        customPrompt: payload.customPrompt,
        ...(typeof TEMPLATE_TYPE !== 'undefined' && TEMPLATE_TYPE.trim()
          ? {
              template: TEMPLATE_TYPE.trim(),
              petName: this.uploadWrap.querySelector('input.pet-name').value.trim()
            }
          : {})
      };

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(generationPayload)
      });

      if (!response.ok) {
        let errorMessage = '';

        try {
          const responseJson = await response.clone().json();
          errorMessage = responseJson?.message || responseJson?.error || responseJson?.detail || responseJson?.result?.message || '';
        } catch (jsonError) {
          try {
            const responseText = await response.text();
            errorMessage = responseText?.trim() || '';
          } catch (textError) {
            errorMessage = '';
          }
        }

        throw new Error(errorMessage || `AI image generation failed with status ${response.status}`);
      }

      const responseJson = await response.json();

      // Map the response to expected format
      // Handle both direct generatedUrl and nested result.generatedUrl
      const generatedUrl = responseJson.generatedUrl || responseJson.result?.generatedUrl || '';
      const previewUrl = responseJson.previewUrl || responseJson.result?.previewUrl || '';
      
      return {
        success: responseJson.success !== false,
        previewUrl: previewUrl,
        imageUrl: generatedUrl,
        sourceImageUrl: uploadedImageUrl,
        style: responseJson.style || styleValue,
        prompt: responseJson.prompt || responseJson.result?.prompt || payload.customPrompt,
        jobId: responseJson.jobId || responseJson.result?.jobId,
        originalImageUrl: responseJson.originalImageUrl || responseJson.result?.originalImageUrl || uploadedImageUrl,
        generatedAt: responseJson.completedAt || new Date().toISOString(),
        fullResponse: responseJson
      };
    } catch (error) {
      console.error('AI preview generation failed:', error);
      throw error;
    }
  }

  /**
   * Caches the AI preview modal sub-elements and binds one-time event handlers when needed.
   *
   * @returns {{
   *   modal?: HTMLElement,
   *   title?: HTMLElement,
   *   status?: HTMLElement,
   *   imageFrame?: HTMLElement,
   *   image?: HTMLImageElement,
   *   prompt?: HTMLTextAreaElement|HTMLInputElement,
   *   promptWrap?: HTMLElement,
   *   customizeToggle?: HTMLElement,
   *   presetButtons?: NodeListOf<HTMLElement>,
   *   regenerateButton?: HTMLButtonElement,
   *   nextStepButton?: HTMLButtonElement,
   *   changeImageSection?: HTMLElement,
   *   changeImageUpload?: HTMLElement
   * }}
   */
  ensureGeneratedPreviewElements() {
    const modal = document.querySelector('[data-ai-generated-modal]');
    if (!modal) return {};

    this.ensureModalImageReplaceSection(modal);

    if (!modal.dataset.bound) {
      const overlay = modal.querySelector('[data-ai-generated-close-overlay]');
      const closeButtons = modal.querySelectorAll('[data-ai-generated-close]');
      const regenerateButton = modal.querySelector('[data-ai-generated-regenerate]');
      const nextStepButton = modal.querySelector('[data-ai-generated-next-step]');
      const promptInput = modal.querySelector('[data-ai-generated-prompt]');
      const promptWrap = modal.querySelector('[data-ai-generated-prompt-wrap]');
      const customizeToggle = modal.querySelector('[data-ai-generated-customize-toggle]');
      const presetButtons = modal.querySelectorAll('[data-ai-generated-preset]');
      const modalTitle = modal.querySelector('[data-ai-generated-title]') || modal.querySelector('h2') || modal.querySelector('h3');

      // Set modal title
      if (modalTitle && !modalTitle.textContent.includes('Your Masterpiece is Ready!')) {
        modalTitle.textContent = 'Your Masterpiece is Ready!';
      }

      overlay?.addEventListener('click', () => this.closeGeneratedPreviewModal());
      closeButtons.forEach(button => {
        button.addEventListener('click', () => this.closeGeneratedPreviewModal());
      });
      customizeToggle?.addEventListener('click', () => {
        const shouldShowPrompt = !!promptWrap?.hidden;
        this.setGeneratedPreviewPromptVisibility(shouldShowPrompt, shouldShowPrompt);
      });
      regenerateButton?.addEventListener('click', () => {
        if (!this.generatedPreviewState.loading) {
          this.closeGeneratedPreviewModal();
          this.generatePreviewForStyle(this.selectedGenerationStyle, this.getSelectedGenerationOptionElement());
        }
      });
      nextStepButton?.addEventListener('click', () => {
        if (nextStepButton.disabled) return;

        this.closeGeneratedPreviewModal();
        this.nextBtn?.dispatchEvent(new MouseEvent('click', { bubbles: true }));
      });
      promptInput?.addEventListener('input', (event) => {
        this.generatedPreviewState.prompt = event.target.value;
        this.syncPromptPresetSelection(event.target.value);
        if (event.target.value) {
          this.stopPromptPlaceholderCycle(true);
        }
      });
      promptInput?.addEventListener('focus', () => {
        this.stopPromptPlaceholderCycle(true);
      });
      promptInput?.addEventListener('blur', (event) => {
        if (!event.target.value.trim()) {
          this.startPromptPlaceholderCycle(event.target);
        }
      });
      presetButtons.forEach(button => {
        button.addEventListener('click', () => {
          this.applyPromptPreset(button.dataset.aiGeneratedPreset);
        });
      });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && modal.classList.contains('is-open')) {
          this.closeGeneratedPreviewModal();
        }
      });

      modal.dataset.bound = 'true';
    }

    return {
      modal,
      title: modal.querySelector('[data-ai-generated-title]') || modal.querySelector('h2') || modal.querySelector('h3'),
      status: modal.querySelector('[data-ai-generated-status]'),
      imageFrame: modal.querySelector('[data-ai-generated-image-frame]'),
      image: modal.querySelector('[data-ai-generated-image]'),
      prompt: modal.querySelector('[data-ai-generated-prompt]'),
      promptWrap: modal.querySelector('[data-ai-generated-prompt-wrap]'),
      customizeToggle: modal.querySelector('[data-ai-generated-customize-toggle]'),
      presetButtons: modal.querySelectorAll('[data-ai-generated-preset]'),
      regenerateButton: modal.querySelector('[data-ai-generated-regenerate]'),
      nextStepButton: modal.querySelector('[data-ai-generated-next-step]'),
      changeImageSection: modal.querySelector('.ai-change-image'),
      changeImageUpload: modal.querySelector('.ai-upload-replace')
    };
  }

  /**
   * Shows or hides the custom prompt UI inside the generated-preview modal.
   *
   * @param {boolean} isVisible
   * @param {boolean} [shouldFocus=false]
   * @returns {void}
   */
  setGeneratedPreviewPromptVisibility(isVisible, shouldFocus=false) {
    const { prompt, promptWrap, customizeToggle } = this.ensureGeneratedPreviewElements();
    if (!promptWrap || !customizeToggle) return;

    promptWrap.hidden = !isVisible;
    customizeToggle.classList.toggle('is-active', isVisible);
    customizeToggle.setAttribute('aria-expanded', isVisible ? 'true' : 'false');

    if (!isVisible) {
      this.stopPromptPlaceholderCycle(true);
      return;
    }

    if (!prompt) return;

    if (!prompt.value.trim()) {
      this.startPromptPlaceholderCycle(prompt);
    }
  }

  /**
   * Returns the canned helper text associated with a prompt preset.
   *
   * @param {string} preset
   * @returns {string}
   */
  getPromptPresetText(preset) {
    const presetMap = {
      masculine: 'Make the portrait feel more masculine.',
      feminine: 'Make the portrait feel more feminine.'
    };

    return presetMap[preset] || '';
  }

  /**
   * Updates the selected preset state based on the current prompt contents.
   *
   * @param {string} [promptValue='']
   * @returns {void}
   */
  syncPromptPresetSelection(promptValue='') {
    const { presetButtons } = this.ensureGeneratedPreviewElements();
    const activePreset = this.isGenerationPresetAllowed(this.selectedGenerationPreset) ? this.selectedGenerationPreset : '';
    this.selectedGenerationPreset = activePreset;
    this.updateGeneratePresetButtons();
    if (!presetButtons?.length) return;

    presetButtons.forEach(button => {
      button.classList.toggle('is-selected', button.dataset.aiGeneratedPreset === activePreset);
    });
  }

  /**
   * Stores the active preset choice and refreshes related preset button UI.
   *
   * @param {string} preset
   * @returns {void}
   */
  setGenerationPreset(preset) {
    if (!this.isGenerationPresetAllowed(preset)) return;
    this.selectedGenerationPreset = preset;

    // Set Gender property based on preset selection
    if (this.items[0]?.properties) {
      this.items[0].properties['Gender'] = capitalize(preset);
    }

    const { prompt } = this.ensureGeneratedPreviewElements();
    if (prompt) {
      prompt.value = this.stripGenerationPresetText(this.generatedPreviewState.prompt || '');
    }

    this.syncPromptPresetSelection(this.generatedPreviewState.prompt || '');
    this.updateGenerateButtonState();
  }

  /**
   * Applies or clears a prompt preset inside the modal input while preserving custom text.
   *
   * @param {string} preset
   * @returns {void}
   */
  applyPromptPreset(preset) {
    const { prompt } = this.ensureGeneratedPreviewElements();
    if (!prompt) return;
    if (!this.isGenerationPresetAllowed(preset)) return;

    let nextPrompt = this.stripGenerationPresetText(prompt.value || '');

    prompt.value = nextPrompt;
    this.generatedPreviewState.prompt = nextPrompt;
    this.selectedGenerationPreset = preset;
    
    // Set Gender property based on preset selection
    if (this.items[0]?.properties) {
      this.items[0].properties['Gender'] = capitalize(preset);
    }
    
    this.syncPromptPresetSelection(nextPrompt);
    this.stopPromptPlaceholderCycle(true);
    prompt.dispatchEvent(new Event('input', { bubbles: true }));
  }
  /**
   * Collects the inline generated-preview elements rendered in the step flow.
   *
   * @returns {{container?: HTMLElement, image?: HTMLImageElement, placeholder?: HTMLElement, loading?: HTMLElement}}
   */
  ensureInlineGeneratedPreviewElements() {
    const sizeStepName = this.product.options[this.getSizeOptionIndex()] || '';
    const sizeStepSelector = sizeStepName ? this.getStepSelector(sizeStepName.toLowerCase()) : null;
    const sizeStepElement = sizeStepSelector ? (this.querySelector(sizeStepSelector) || document.querySelector(sizeStepSelector)) : null;
    const lastStepName = this.steps?.[this.steps.length - 1];
    const lastStepSelector = lastStepName ? this.getStepSelector(lastStepName) : null;
    const lastStepElement = lastStepSelector ? (this.querySelector(lastStepSelector) || document.querySelector(lastStepSelector)) : null;
    const hostElement = sizeStepElement || lastStepElement;
    const container = hostElement?.querySelector('[data-generated-preview-inline]');
    if (!container) return {};

    return {
      container,
      badge: container.querySelector('[data-generated-preview-inline-badge]'),
      title: container.querySelector('.step-sizing-ai__generated-preview-title'),
      status: container.querySelector('[data-generated-preview-inline-status]'),
      loading: container.querySelector('[data-generated-preview-inline-loading]'),
      image: container.querySelector('[data-generated-preview-inline-image]'),
      editButton: container.querySelector('[data-generated-preview-inline-edit]')
    };
  }

  /**
   * Rotates the inline loading message shown while generation is in progress.
   *
   * @param {HTMLElement} loadingElement
   * @returns {void}
   */
  updateInlineLoadingMessage(loadingElement) {
    if (!loadingElement) return;

    loadingElement.textContent = AI_INLINE_LOADING_MESSAGES[this.inlineLoadingMessageIndex];
    this.inlineLoadingMessageIndex = (this.inlineLoadingMessageIndex + 1) % AI_INLINE_LOADING_MESSAGES.length;
  }

  /**
   * Starts the interval that cycles inline loading copy during generation.
   *
   * @param {HTMLElement} loadingElement
   * @returns {void}
   */
  startInlineLoadingMessages(loadingElement) {
    if (!loadingElement || this.inlineLoadingMessageInterval) return;

    this.inlineLoadingMessageIndex = 0;
    this.updateInlineLoadingMessage(loadingElement);
    this.inlineLoadingMessageInterval = window.setInterval(() => {
      this.updateInlineLoadingMessage(loadingElement);
    }, 5000);
  }

  /**
   * Stops the inline loading-message cycle and clears temporary loading text.
   *
   * @param {HTMLElement} loadingElement
   * @returns {void}
   */
  stopInlineLoadingMessages(loadingElement) {
    if (this.inlineLoadingMessageInterval) {
      window.clearInterval(this.inlineLoadingMessageInterval);
      this.inlineLoadingMessageInterval = null;
    }

    if (loadingElement) {
      loadingElement.textContent = '';
    }
  }

  /**
   * Synchronizes the inline preview block with the current generated-preview state.
   *
   * @returns {void}
   */
  updateInlineGeneratedPreview() {
    const container = document.querySelector('[data-generated-preview-inline]');
    const image = document.querySelector('[data-generated-preview-inline-image]');
    const badge = document.querySelector('[data-generated-preview-inline-badge]');
    const title = document.querySelector('.step-sizing-ai__generated-preview-title');
    const status = document.querySelector('[data-generated-preview-inline-status]');
    const loading = document.querySelector('[data-generated-preview-inline-loading]');
    const editButton = document.querySelector('[data-generated-preview-inline-edit]');
    if (!container || !image) return;

    if (!container.dataset.bound && editButton) {
      editButton.addEventListener('click', () => {
        this.openGeneratedPreviewModal();
      });
      container.dataset.bound = 'true';
    }

    const previewDisplayUrl = this.generatedPreviewState.previewUrl || this.generatedPreviewState.imageUrl || '';
    const hasImage = !!previewDisplayUrl;
    const isLoading = !!this.generatedPreviewState.loading;
    const hasError = !!this.generatedPreviewState.errorMessage && !isLoading && !hasImage;
    const styleLabel = this.selectedGenerationStyle || this.generatedPreviewState.style || 'AI Portrait';
    const presetLabel = this.selectedGenerationPreset ? capitalize(this.selectedGenerationPreset) : '';

    if (isLoading) {
      container.hidden = false;
      container.classList.add('is-loading');
      this.startInlineLoadingMessages(loading);
    }

    if (badge) {
      badge.textContent = [styleLabel, presetLabel].filter(Boolean).join(' | ') || 'AI Portrait Preview';
    }

    if (title) {
      title.textContent = isLoading
        ? 'Creating Your Masterpiece...'
        : hasError
          ? 'Preview Generation Failed'
        : hasImage
          ? 'Your Masterpiece is Ready!'
          : 'Your Masterpiece Will Appear Soon';
    }

    if (status) {
      status.textContent = isLoading
        ? `Generating your ${styleLabel} preview...`
        : hasError
          ? this.generatedPreviewState.errorMessage
        : hasImage
          ? 'Here is your portrait preview, please note, watermark will be removed if you purchase digital or physical print.'
          : 'Your generated portrait will appear here once it is ready.';
    }

    if (hasImage) {
      container.hidden = false;
      container.classList.remove('is-loading');
      this.stopInlineLoadingMessages(loading);
      image.src = previewDisplayUrl;
      image.alt = `Generated ${styleLabel} portrait preview`;
      image.hidden = false;
      image.style.display = 'block';
    } else if (hasError) {
      container.hidden = false;
      container.classList.remove('is-loading');
      this.stopInlineLoadingMessages(loading);
      image.removeAttribute('src');
      image.hidden = true;
      image.style.display = 'none';
    } else if (!isLoading) {
      container.hidden = true;
      container.classList.remove('is-loading');
      this.stopInlineLoadingMessages(loading);
      image.removeAttribute('src');
      image.hidden = true;
      image.style.display = 'none';
    }

    if (editButton) {
      editButton.disabled = isLoading || !hasImage;
    }
  }

  /**
   * Maps API/network failures into a readable preview-generation message.
   *
   * @param {any} error
   * @returns {string}
   */
  getGeneratedPreviewErrorMessage(error) {
    const rawMessage = error?.message || '';
    const message = rawMessage.trim();

    if (!message) {
      return 'We could not generate the preview right now. Please try again in a moment.';
    }

    if (/failed to fetch|networkerror|network request failed/i.test(message)) {
      return 'We could not reach the preview service. Please check your connection and try again.';
    }

    if (/no image url in response/i.test(message)) {
      return 'The preview service finished without returning an image. Please try again.';
    }

    if (/status 4\d{2}|status 5\d{2}/i.test(message)) {
      return 'We could not generate the preview right now. Please try again in a moment.';
    }

    return message;
  }

  /**
   * Toggles preview-related affordances based on whether a style and uploaded image are available.
   *
   * @returns {void}
   */
  syncPreviewAvailabilityState() {
    const hasPreview = !!(this.generatedPreviewState.previewUrl || this.generatedPreviewState.imageUrl);
    this.dataset['hasPreview'] = hasPreview ? 'true' : 'false';
  }

  /**
   * Builds the placeholder message cycle used for the custom prompt input.
   *
   * @param {HTMLInputElement|HTMLTextAreaElement} prompt
   * @returns {string[]}
   */
  getPromptPlaceholderMessages(prompt) {
    if (!prompt) return [];

    try {
      const messages = JSON.parse(prompt.dataset.placeholderMessages || '[]');
      return Array.isArray(messages) ? messages.filter(Boolean) : [];
    } catch (error) {
      console.error('Failed to parse prompt placeholder messages.', error);
      return [];
    }
  }

  /**
   * Advances the prompt placeholder to the next helper message.
   *
   * @param {HTMLInputElement|HTMLTextAreaElement} prompt
   * @returns {void}
   */
  updatePromptPlaceholder(prompt) {
    const messages = this.getPromptPlaceholderMessages(prompt);
    if (!messages.length || !prompt) return;

    prompt.placeholder = messages[this.promptPlaceholderIndex];
    this.promptPlaceholderIndex = (this.promptPlaceholderIndex + 1) % messages.length;
  }

  /**
   * Starts cycling helper placeholder text while the prompt input is empty.
   *
   * @param {HTMLInputElement|HTMLTextAreaElement} prompt
   * @returns {void}
   */
  startPromptPlaceholderCycle(prompt) {
    if (!prompt || this.promptPlaceholderInterval || document.activeElement === prompt || prompt.value.trim()) {
      return;
    }

    this.updatePromptPlaceholder(prompt);
    this.promptPlaceholderInterval = window.setInterval(() => {
      this.updatePromptPlaceholder(prompt);
    }, 3000);
  }

  /**
   * Stops cycling prompt placeholders and optionally clears the current placeholder text.
   *
   * @param {boolean} [clearPlaceholder=false]
   * @returns {void}
   */
  stopPromptPlaceholderCycle(clearPlaceholder=false) {
    if (this.promptPlaceholderInterval) {
      window.clearInterval(this.promptPlaceholderInterval);
      this.promptPlaceholderInterval = null;
    }

    if (clearPlaceholder) {
      const { prompt } = this.ensureGeneratedPreviewElements();
      if (prompt) {
        prompt.placeholder = '';
      }
    }
  }

  /**
   * Opens the generated-preview modal and synchronizes its controls with current state.
   *
   * @returns {void}
   */
  openGeneratedPreviewModal() {
    const { modal, prompt, regenerateButton, nextStepButton, image, imageFrame, status } = this.ensureGeneratedPreviewElements();
    if (!modal) return;

    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('ai-generated-modal-open');
    this.setGeneratedPreviewPromptVisibility(false, false);

    if (prompt && document.activeElement !== prompt) {
      prompt.value = this.generatedPreviewState.prompt || '';
      this.syncPromptPresetSelection(prompt.value);
    }

    const previewDisplayUrl = this.generatedPreviewState.previewUrl || this.generatedPreviewState.imageUrl || '';
    const errorMessage = this.generatedPreviewState.errorMessage || '';
    if (image) {
      if (previewDisplayUrl) {
        image.src = previewDisplayUrl;
        image.alt = `Generated ${(this.selectedGenerationStyle || this.generatedPreviewState.style || 'AI Portrait')} portrait`;
        imageFrame?.classList.add('has-image');
        if (status && !this.generatedPreviewState.loading) {
          status.textContent = `${this.selectedGenerationStyle || this.generatedPreviewState.style || 'AI Portrait'} portrait is ready`;
        }
      } else {
        image.removeAttribute('src');
        imageFrame?.classList.remove('has-image');
        if (status && errorMessage && !this.generatedPreviewState.loading) {
          status.textContent = errorMessage;
        }
      }
    }

    if (regenerateButton) {
      regenerateButton.disabled = this.generatedPreviewState.loading || !this.selectedGenerationStyle;
    }
    if (nextStepButton) {
      nextStepButton.disabled = this.generatedPreviewState.loading || !this.generatedPreviewState.imageUrl || !this.canAdvanceFromPreview();
    }
  }

  /**
   * Closes the generated-preview modal and clears modal-only animation state.
   *
   * @returns {void}
   */
  closeGeneratedPreviewModal() {
    const { modal } = this.ensureGeneratedPreviewElements();
    if (!modal) return;

    this.stopPromptPlaceholderCycle(false);

    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('ai-generated-modal-open');
  }

  /**
   * Checks whether the builder has another step after the current one.
   *
   * @returns {boolean}
   */
  canAdvanceFromPreview() {
    const currentIndex = parseInt(this.dataset['index'], 10);
    if (Number.isNaN(currentIndex)) return false;

    return currentIndex < this.steps.length - 1;
  }

  /**
   * Updates preview UI into its loading state for the requested style.
   *
   * @param {string} styleValue
   * @param {string} [previewUrl='']
   * @returns {void}
   */
  setGeneratedPreviewLoading(styleValue, previewUrl='') {
    const { modal, status, image, imageFrame, regenerateButton, nextStepButton } = this.ensureGeneratedPreviewElements();

    if (modal?.classList.contains('is-open') && status && image) {
      status.textContent = `Generating ${styleValue} portrait...`;
      imageFrame?.classList.remove('has-image');
      image.removeAttribute('src');
    }

    this.generatedPreviewState.imageUrl = '';
    this.generatedPreviewState.previewUrl =  '';
    this.generatedPreviewState.errorMessage = '';

    if (regenerateButton) {
      regenerateButton.disabled = true;
      regenerateButton.textContent = 'Generating...';
    }
    if (nextStepButton) {
      nextStepButton.disabled = true;
    }
    this.updateInlineGeneratedPreview();
    window.setTimeout(() => {
      this.updateInlineGeneratedPreview();
    }, 50);
    this.updateGenerateButtonState();
    this.syncPreviewAvailabilityState();
  }

  /**
   * Writes a successful generated image into both modal and inline preview UI.
   *
   * @param {string} imageUrl
   * @param {string} styleValue
   * @param {string} [previewUrl='']
   * @returns {void}
   */
  setGeneratedPreviewImage(imageUrl, styleValue, previewUrl='') {
    this.generatedPreviewState.imageUrl = imageUrl;
    this.generatedPreviewState.previewUrl = previewUrl || this.generatedPreviewState.previewUrl || '';
    this.generatedPreviewState.errorMessage = '';
    this.updateInlineGeneratedPreview();
    window.setTimeout(() => {
      this.updateInlineGeneratedPreview();
      const inlineContainer = document.querySelector('[data-generated-preview-inline]');
      inlineContainer?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);

    const { modal, status, image, imageFrame, regenerateButton, nextStepButton, prompt, promptWrap } = this.ensureGeneratedPreviewElements();
    const previewDisplayUrl = this.generatedPreviewState.previewUrl || this.generatedPreviewState.imageUrl || '';
    if (!status || !image || !previewDisplayUrl) return;

    status.textContent = `${styleValue} portrait is ready`;
    image.src = previewDisplayUrl;
    image.alt = `Generated ${styleValue} portrait`;
    imageFrame?.classList.add('has-image');

    if (modal?.classList.contains('is-open')) {
      this.openGeneratedPreviewModal();
    }
    
    if (prompt) {
      prompt.value = this.generatedPreviewState.prompt || '';
      this.syncPromptPresetSelection(prompt.value);
      if (!promptWrap?.hidden) {
        prompt.focus();
      }
    }
    
    if (regenerateButton) {
      regenerateButton.disabled = !this.selectedGenerationStyle;
      regenerateButton.textContent = 'Regenerate With Customized Prompt';
      regenerateButton.setAttribute('title', 'Regenerate with your custom prompt above');
    }
    if (nextStepButton) {
      nextStepButton.disabled = !this.canAdvanceFromPreview();
    }
    
    this.updateInlineGeneratedPreview();
    window.setTimeout(() => {
      this.updateInlineGeneratedPreview();
    }, 50);
    this.updateGenerateButtonState();
  }

  /**
   * Removes generated-preview metadata keys from the main line-item properties object.
   *
   * @param {string[]} [keys=['_Generated Image', '_Preview Image', 'Style', '_Original Image URL', 'Gender']]
   * @returns {void}
   */
  clearGeneratedPreviewProperties(keys=['_Generated Image', '_Preview Image', 'Style', '_Original Image URL', 'Gender']) {
    const properties = this.items?.[0]?.properties;
    if (!properties) return;

    keys.forEach(key => {
      delete properties[key];
    });
  }

  /**
   * Removes transient/internal properties from a cart line item before checkout submission.
   *
   * These keys are used to manage the step-builder UI and upload pipeline but should not be
   * persisted as customer-facing line item properties on the final cart item.
   *
   * @param {Record<string, any>} [properties={}] Raw line item properties collected by the builder.
   * @returns {Record<string, any>} Shallow-cloned property object with internal keys removed.
   */
  sanitizeCartItemProperties(properties = {}) {
    const sanitizedProperties = { ...properties };
    ['_Pet photo', '_Pet photo (direct link)', '__step-product'].forEach((key) => {
      delete sanitizedProperties[key];
    });
    return sanitizedProperties;
  }

  /**
   * Resets all generated-preview UI and property state after a style/image change.
   *
   * @param {string} [styleValue=this.selectedGenerationStyle]
   * @returns {void}
   */
  resetGeneratedPreviewState(styleValue=this.selectedGenerationStyle) {
    this.generatedPreviewState.loading = false;
    this.generatedPreviewState.imageUrl = '';
    this.generatedPreviewState.previewUrl = '';
    this.generatedPreviewState.style = styleValue || '';
    this.generatedPreviewState.errorMessage = '';
    setPageInteractionBlocked('ai-generation', false);

    this.clearGeneratedPreviewProperties();
    this.closeGeneratedPreviewModal();
    this.setGeneratedPreviewPromptVisibility(false, false);
    this.syncGenerationPresetAvailability(styleValue || '');

    const { status, image, imageFrame, regenerateButton, nextStepButton } = this.ensureGeneratedPreviewElements();
    if (status) {
      status.textContent = 'Your generated portrait will appear here once it is ready.';
    }
    if (image) {
      image.removeAttribute('src');
      // image.hidden = true;
      // image.style.display = 'none';
    }
    imageFrame?.classList.remove('has-image');

    if (regenerateButton) {
      regenerateButton.disabled = !this.selectedGenerationStyle;
      regenerateButton.textContent = 'Regenerate With Customized Prompt';
      regenerateButton.setAttribute('title', 'Regenerate with your custom prompt above');
    }
    if (nextStepButton) {
      nextStepButton.disabled = true;
    }

    this.updateInlineGeneratedPreview();
    this.updateGenerateButtonState();
    this.syncPreviewAvailabilityState();
  }

  /**
   * Clears stale generation results when the uploaded source image changes.
   *
   * @param {string} nextImageUrl Newly detected uploaded image URL.
   * @returns {void}
   */
  handleUploadedImageChange(nextImageUrl) {
    if (!nextImageUrl) return;

    this.syncUploadPropertiesFromInputs();

    this.generatedPreviewState.imageUrl = '';
    this.generatedPreviewState.previewUrl = '';
    this.generatedPreviewState.loading = false;
    this.generatedPreviewState.errorMessage = '';
    setPageInteractionBlocked('ai-generation', false);
    this.clearGeneratedPreviewProperties(['_Generated Image', '_Preview Image', '_Original Image URL']);

    const { modal, status, image, imageFrame, regenerateButton, nextStepButton } = this.ensureGeneratedPreviewElements();
    if (image) {
      image.removeAttribute('src');
      // image.hidden = true;
      // image.style.display = 'none';
    }
    imageFrame?.classList.remove('has-image');

    if (status) {
      status.textContent = 'New image uploaded. Click regenerate.';
    }

    if (regenerateButton) {
      regenerateButton.disabled = !this.selectedGenerationStyle;
      regenerateButton.textContent = 'Regenerate With Customized Prompt';
      regenerateButton.setAttribute('title', 'Regenerate with your custom prompt above');
    }

    if (nextStepButton) {
      nextStepButton.disabled = true;
    }

    this.updateInlineGeneratedPreview();
    this.updateGenerateButtonState();
    this.syncPreviewAvailabilityState();

    if (modal) {
      modal.scrollTop = 0;
    }
  }

  /**
   * Starts polling for uploaded-image changes so previews can be invalidated automatically.
   *
   * @returns {void}
   */
  startUploadChangeWatcher() {
    if (this.uploadChangeWatchInterval) return;

    this.lastKnownUploadedImageUrl = this.getUploadedImageUrl() || '';
    this.uploadChangeWatchInterval = window.setInterval(() => {
      const nextUploadedImageUrl = this.getUploadedImageUrl() || '';
      if (!nextUploadedImageUrl || nextUploadedImageUrl === this.lastKnownUploadedImageUrl) {
        return;
      }

      this.lastKnownUploadedImageUrl = nextUploadedImageUrl;
      this.handleUploadedImageChange(nextUploadedImageUrl);
    }, 400);
  }

  

  /**
   * Runs the end-to-end AI preview generation flow for the selected style.
   *
   * Expects an uploaded image to exist before it is called.
   *
   * @param {string} styleValue
   * @param {HTMLElement|null} [optionElement=null]
   * @returns {Promise<void>}
   */
  async generatePreviewForStyle(styleValue, optionElement=null) {
    if (!styleValue || this.generatedPreviewState.loading) return;

    const uploadedImageUrl = this.getUploadedImageUrl();
    if (!uploadedImageUrl) return;

    const stylePreviewUrl = this.getStylePreviewImageUrl(optionElement);

    this.generatedPreviewState.loading = true;
    this.generatedPreviewState.style = styleValue;
    this.generatedPreviewState.imageUrl = '';
    this.generatedPreviewState.previewUrl = '';
    this.generatedPreviewState.errorMessage = '';
    setPageInteractionBlocked('ai-generation', true);
    this.setGeneratedPreviewLoading(styleValue);

    try {
      const response = await this.requestGeneratedPreview(styleValue, optionElement);
      
      if (!response?.imageUrl) {
        throw new Error(
          response?.fullResponse?.message
          || response?.fullResponse?.error
          || response?.fullResponse?.detail
          || 'No image URL in response'
        );
      }

      this.generatedPreviewState.imageUrl = response.imageUrl;
      this.updateInlineGeneratedPreview();
      window.setTimeout(() => {
        this.updateInlineGeneratedPreview();
      }, 50);

      // this.generatedPreviewState.prompt = response.prompt || this.generatedPreviewState.prompt || '';
      this.setGeneratedPreviewImage(response.imageUrl, styleValue, response.previewUrl || stylePreviewUrl);
      
      // Store all response data in items
      // this.items[0].properties['_generated_preview_url'] = response.imageUrl;
      // this.items[0].properties['_ai_style'] = styleValue;
      // // this.items[0].properties['_ai_custom_prompt'] = this.generatedPreviewState.prompt;
      // this.items[0].properties['_ai_job_id'] = response.jobId || '';
      // this.items[0].properties['_ai_original_image_url'] = response.originalImageUrl || '';
      // this.items[0].properties['_ai_generated_at'] = response.generatedAt || '';


      this.items[0].properties['_Generated Image'] = response.imageUrl;
      this.items[0].properties['_Preview Image'] = response.previewUrl || '';
      this.items[0].properties['_Original Image URL'] = response.originalImageUrl || '';
      this.items[0].properties['Style'] = styleValue;
      if (typeof HIDE_GENDER_OPTIONS === 'undefined' || !HIDE_GENDER_OPTIONS) {
        this.items[0].properties['Gender'] = capitalize(this.selectedGenerationPreset);
      } else {
        delete this.items[0].properties['Gender'];
      }
      
    } catch (error) {
      console.error('AI preview generation failed.', error);
      const errorMessage = this.getGeneratedPreviewErrorMessage(error);
      const { status, regenerateButton } = this.ensureGeneratedPreviewElements();
      // this.generatedPreviewState.errorMessage = errorMessage;
      this.generatedPreviewState.errorMessage = 'We could not generate the portrait. Please try again with other styles.';
      if (status) {
        status.textContent = 'We could not generate the portrait. Please try again with other styles.';
      }
      this.generatedPreviewState.imageUrl = '';
      this.generatedPreviewState.previewUrl = '';
      this.updateInlineGeneratedPreview();
      if (regenerateButton) {
        regenerateButton.disabled = !this.selectedGenerationStyle;
        regenerateButton.textContent = 'Regenerate With Customized Prompt';
      }
    } finally {
      this.generatedPreviewState.loading = false;
      setPageInteractionBlocked('ai-generation', false);
      this.updateInlineGeneratedPreview();
      this.updateGenerateButtonState();
      this.syncPreviewAvailabilityState();
    }
  }

  /**
   * Mirrors a resolved variant option value back into the option-item selection UI.
   *
   * @param {number} stepIndex Product option index.
   * @param {string} selectedValue Resolved option value.
   * @returns {void}
   */
  syncSelectedOption(stepIndex, selectedValue) {
    const stepName = this.product.options[stepIndex];
    if (!stepName) return;

    document.querySelectorAll(`[data-step="${stepName}"] option-item`).forEach(option => {
      option.classList.toggle('selected', option.dataset['v_option'] == selectedValue);
    });
  }

  /**
   * Recomputes and renders the compare-at/was price for the configured bundle.
   *
   * @returns {void}
   */
  updateWasPrice() {
  if (!this.cur_variant) return;

  const subCents = this.items.reduce((s, i) => s + (parseInt(i.price) || 0), 0);

  const extrasCents = this.items.slice(1).reduce((s, i) => s + (parseInt(i.price) || 0), 0);

  const mainPrice    = parseInt(this.cur_variant.price) || 0;
  const mainCompare  = parseInt(this.cur_variant.compare_at_price) || 0;
  const mainWasCents = mainCompare > mainPrice ? mainCompare : mainPrice;

  const wasCents = mainWasCents + extrasCents;

  const holder  = this.querySelector('[data-compare-holder]') || this.querySelector('.subtotal--compare-price');
  const moneyEl = this.querySelector('[data-compare-money]')   || this.querySelector('.compare--price-money');

  if (!holder || !moneyEl) return;

  if (wasCents > subCents) {
    holder.style.display = '';
    moneyEl.textContent = `${this.cur_symbol}${(wasCents / 100).toFixed(2)}`;
  } else {
    holder.style.display = 'none';
  }
}

  /**
   * Binds the custom events and CTA handlers that drive the builder state machine.
   *
   * @returns {void}
   */
  addActions() {
    this.addEventListener('selectopt', this.onSelectOption.bind(this));
    this.addEventListener('before', this.onBeforeStep.bind(this));
    this.addEventListener('next', this.onNextStep.bind(this));
    this.addEventListener('addextra', this.onAddExtra.bind(this));
    this.addEventListener('removeextra', this.onRemoveExtra.bind(this));
    this.addEventListener('customaddcart', this.onAddCart);
    this.addCartBtn.addEventListener('click',() => {
      this.dispatchEvent(this.addCartEv);
    });
  }

  /**
   * Handles a step option selection by updating variant state and generation-related UI.
   *
   * @param {CustomEvent|Event} ev
   * @returns {void}
   */
  onSelectOption(ev) {
    const optionName = ev.target.dataset['option'];
    const cur_index = this.product.options.findIndex(option => option === optionName);
    const value = ev.target.v_option;
    const generationStepName = this.getGenerationStepName();

    if (optionName === generationStepName) {
      const didGenerationStyleChange = this.normalizeGenerationValue(this.selectedGenerationStyle) !== this.normalizeGenerationValue(value);
      this.selectedGenerationStyle = value;

      if (didGenerationStyleChange) {
        this.resetGeneratedPreviewState(value);
      } else {
        this.syncGenerationPresetAvailability(value);
        this.updateGenerateButtonState();
      }
    }

    if (cur_index === -1) {
      return;
    }

    if (cur_index === this.getSizeOptionIndex()) {
      this.hasExplicitSizeSelection = true;
    }

    this.updateVariant(cur_index, value);
  }
  
  /**
   * Moves the builder to the previous visible step.
   *
   * @param {Event} ev
   * @returns {void}
   */
  onBeforeStep(ev) {
    // debugger;
    let index = ev.target.dataset['index'];
    index > 0 ? index--: index;
    this.setStep(index);
  }
  
  /**
   * Validates the current step and advances to the next visible step when allowed.
   *
   * @param {Event} ev
   * @returns {void}
   */
  onNextStep(ev) {
    let index = parseInt(ev.target.dataset['index'], 10);
    let current = this.steps[index];
    let limit = this.steps.length;
    limit--;

    index < limit ? index++: index;
    const nextStepName = this.steps[index];

    if (this.isFormatStep(nextStepName) && !this.generatedPreviewState.imageUrl) {
      this.updateInlineGeneratedPreview();
      this.triggerGenerateActionShake();
      const inlineStatus = document.querySelector('[data-generated-preview-inline-status]');
      if (inlineStatus) {
        inlineStatus.textContent = 'Generate your preview first before choosing a format.';
      }
      return;
    }

    if(current == 'upload') {
      var validated = true;
      var imageAttrContainers = $(this).find('.uploadkit-attribute-container');

      if (window.UploadKit && imageAttrContainers.length > 0 ) {
          for(var i=0; i<imageAttrContainers.length; i++) {
              if(imageAttrContainers[i].innerHTML === '') {
                  validated = false;
              }
          }

      }
      
      if(this.items[0]['properties'] == undefined) {
        Object.assign(this.items[0].properties, { ...this.uploadWrap.inputs.properties });
      }

      this.uploadWrap.querySelectorAll('input').forEach(input => {
        let key = input.name.replace('properties[', '').replace(']','');
        this.items[0]['properties'][key] = input.value;
      });

      // Validate pet-name field if it exists
      const petNameInput = this.uploadWrap?.querySelector('input.pet-name');
      if (petNameInput && !petNameInput.value.trim()) {
        validated = false;
      }

      if (validated) {
        this.setStep(index);
        this.updateGenerateButtonState();
        if (petNameInput) {
          petNameInput.classList.remove('input-error');
        }
      } else if (validated == false && petNameInput && !petNameInput.value.trim()) {
        petNameInput.focus();
        petNameInput.classList.add('input-error');
      } else {
          $('.mb_container').fadeIn();
          if (petNameInput) {
            petNameInput.classList.remove('input-error');
          }
      }
    }
    else {
      this.setStep(index);
    }
  }

  /**
   * Activates the requested step, updates related button state, and optionally scrolls into view.
   *
   * @param {number} index Visible-step index to activate.
   * @param {boolean} [scroll=true]
   * @returns {void}
   */
  setStep(index, scroll=true) {
    index = parseInt(index, 10);
    if (Number.isNaN(index) || index < 0) index = 0;
    if (index >= this.steps.length) index = this.steps.length - 1;

    this.dataset['index'] = index;
    this.dataset['current'] = this.steps[index];
    this.dataset['isLastStep'] = index === this.steps.length - 1 ? 'true' : 'false';

    let before, next, limit;

    limit = this.steps.length;
    
    index > 0 ? before = this.steps[index - 1] : before = this.steps[0];
    index < --limit ? next = this.steps[index + 1] : next = this.steps[this.steps.length - 1];

    

    this.beforeBtn.dataset['index'] = index;
    this.beforeBtn.dataset['current'] = this.steps[index];
    this.beforeBtn.dataset['before'] = before;
    this.beforeBtn.dataset['next'] = next;

    this.nextBtn.dataset['index'] = index;
    this.nextBtn.dataset['current'] = this.steps[index];
    this.nextBtn.dataset['before'] = before;
    this.nextBtn.dataset['next'] = next;

    this.querySelectorAll('.step-process').forEach(process => {
      process.classList.remove('active');
      process.classList.remove('current');
    });

    this.getVisibleStepProcesses().forEach((process, arg) => {
      if(arg == index) {
        process.classList.remove('active');
        process.classList.add('current');
      } 
      else if(arg < index) {
        process.classList.add('active');
        process.classList.remove('current');
      }
      else {
        process.classList.remove('active');
        process.classList.remove('current');
      }
    });

    if(this.steps[index] != 'upload') {
      this.updatePremium(this.getVariantStepIndex(this.steps[index]));
    }

    if (scroll) {
      this.scrollToStepProcesses();
    }
    this.updateInlineGeneratedPreview();
    this.updateGenerateButtonState();
  }

  /**
   * Updates the premium badge/copy for the currently active option selection.
   *
   * @param {number} index Product option index.
   * @returns {void}
   */
  updatePremium(index) {
    if (index < 0) return;

    const curOption = this.cur_variant.options;
    let step = this.product.options[index];
    let symbol = this.cur_symbol;
    step = capitalize(step);

    const subVariants = this.product.variants.filter( variant => {
      let isPassed = true;
      let i = 0;

      do {
        if( i != index) {
          variant.options[i] === curOption[i] ? isPassed = true : isPassed = false;
        }
        if(!isPassed) return isPassed;
        i++;
      } while (i < curOption.length);
      return isPassed
    });

    let premiumPrices = {};
    let curPrice = this.cur_variant.price;

    subVariants.forEach( variant => {
      let delta = variant.price - curPrice;
      Object.assign(premiumPrices, {[variant.options[index]]: delta});
    });
  }

  /**
   * Adds an upsell line item to the in-memory cart payload.
   *
   * @param {CustomEvent|Event} ev
   * @returns {void}
   */
  onAddExtra(ev) {
    let v_id = ev.target.dataset['v_id'];
    let v_price = ev.target.dataset['v_price'];
    let item = {
      id: v_id,
      quantity: 1,
      price: v_price,
    };

    this.items.push(item);
    this.updateSubTotal();
  }

  /**
   * Removes an upsell line item from the in-memory cart payload.
   *
   * @param {CustomEvent|Event} ev
   * @returns {void}
   */
  onRemoveExtra(ev) {
    let v_id = ev.target.v_id;
    this.items.forEach((item, arg) => {
      if(item.id == v_id) this.items.splice(arg, 1);
    });
    this.updateSubTotal();
  }

  /**
   * Resolves the nearest valid Shopify variant after an option value changes.
   *
   * It also updates URL state, selected option UI, generated-preview dependencies, and pricing.
   *
   * @param {number} cur_index Product option index that changed.
   * @param {string} value Newly selected option value.
   * @returns {void}
   */
  updateVariant(cur_index, value) {
    if (!this.cur_variant || !this.cur_options) {
      console.warn('Variant update skipped because the current variant state is not initialized.');
      return;
    }

    const generationOptionIndex = this.getGenerationOptionIndex();
    const previousGenerationStyle = generationOptionIndex > -1
      ? (this.cur_variant.options[generationOptionIndex] || '')
      : '';

    let cur_options = this.cur_options.split(',');
    let new_options = [...cur_options];
    new_options[cur_index] = value;

    let subVariants = this.product.variants.filter(
      variant => variant.options[cur_index] == value
    );
    for (let i = 0; i < cur_options.length; i++) {
      if (i !== cur_index) {
        const availableCurOptions = [...new Set(subVariants.map(variant => variant.options[i]))];
        const curSubVariants = [...subVariants.filter(
          variant => variant.options[i] == cur_options[i]
        )];

        if (!curSubVariants.length) {
          new_options[i] = availableCurOptions.pop();
          subVariants = [...subVariants.filter(
            variant => variant.options[i] == new_options[i]
          )];
          this.syncSelectedOption(i, new_options[i]);
        } else {
          subVariants = [...curSubVariants];
        }
      }
    }

    const nextVariant = subVariants.find(variant => variant.options.join() === new_options.join())
      || subVariants.find(variant => variant.available)
      || subVariants[0]
      || this.product.variants.find(variant => variant.options.join() === new_options.join())
      || this.cur_variant;

    if (!nextVariant) {
      console.warn('Variant update skipped because no matching variant was found.', { cur_index, value, new_options });
      this.syncSelectedOption(cur_index, this.cur_variant.options[cur_index]);
      return;
    }

    this.cur_variant = nextVariant;
    this.cur_options = new_options.join();
    this.syncSelectedOption(cur_index, this.cur_variant.options[cur_index]);

    if (generationOptionIndex > -1) {
      const nextGenerationStyle = this.cur_variant.options[generationOptionIndex] || '';
      const didGenerationStyleChange = this.normalizeGenerationValue(previousGenerationStyle) !== this.normalizeGenerationValue(nextGenerationStyle);

      if (didGenerationStyleChange) {
        this.resetGeneratedPreviewState(nextGenerationStyle);
      } else {
        this.syncGenerationPresetAvailability(nextGenerationStyle);
        this.updateGenerateButtonState();
      }
    }

    this.url.searchParams.set("variant", this.cur_variant.id);
    window.history.replaceState({}, "", this.url);

    this.items[0].id = this.cur_variant.id;
    this.items[0].price = this.cur_variant.price;
    
    Object.assign(this.items[0].properties, { ...this.uploadWrap.inputs.properties });

    this.updateSubTotal();

    const currentStep = this.dataset['current'];
    const currentStepIndex = parseInt(this.dataset['index'], 10) || 0;
    const nextSteps = this.getStepsForVariant(nextVariant);
    this.steps = nextSteps;
    this.toggleFrameStepVisibility(nextVariant);
    this.setStep(
      nextSteps.includes(currentStep)
        ? nextSteps.indexOf(currentStep)
        : Math.min(currentStepIndex, nextSteps.length - 1),
      false
    );

    if(this.cur_variant.featured_media) {

      let v_img = this.cur_variant.featured_media.id;
      let slidedImage = false;
  
      document.querySelectorAll('.mySwiper2 .swiper-slide img').forEach( (img, arg) => {
        if(img.dataset['id'] == v_img && slidedImage != true) {
          slidedImage = true;
          window.mySwiper.slideTo(arg);
        }
      });
    }

    this.updatePremium(cur_index);
  }

  /**
   * Finalizes the builder payload and submits the configured line items to Shopify cart/add.
   *
   * This method removes stale free-mug entries, sanitizes the primary line item properties,
   * stamps the payload with a timestamp, conditionally adds the promotional mug variant, and
   * then redirects the customer to checkout after persisting enough step-builder state for
   * the return/reset flow.
   *
   * Expects `this.items` to already contain the main configured product line plus any extras,
   * and relies on `mug-v_id` plus `#step-block-product` markup for the free-mug promotion logic.
   *
   * @returns {void}
   */
  onAddCart() { 
    const items = this.items;
    var free_mug_index = -1;
    for (let index = 0; index < items.length; index++) {
      var item = items[index];
      if (item.properties != undefined) {
        if (item?.properties['__free-mug']){
          free_mug_index = index;
        }
      }
    }
    if (free_mug_index > 0){
      items.splice(free_mug_index, 1);
    }
    
    const sections = 'cart-drawer';

    let product_info = JSON.parse(document.querySelector('#step-block-product').textContent);
    let mug_vt_id = document.querySelector("step-block").getAttribute("mug-v_id");
    let main_item = this.items[0];
    let main_prop = this.sanitizeCartItemProperties(main_item["properties"] || {});
    main_prop["__time"] = Date.now();
    main_item["properties"] = main_prop;
    let mug_prop = JSON.stringify(main_prop);
    mug_prop = JSON.parse(mug_prop);
    mug_prop["__free-mug"] = "true";
    let mug = {
      id: mug_vt_id,
      quantity: 1,
      price: 0,
      properties: mug_prop
    };
    
    let true_mug = false;
    for (let i = 0; i < product_info.variants.length; i++) {
      if (product_info.variants[i].id == main_item.id) {
        let main_variant = product_info.variants[i];
        if (main_variant.option2 != "Unframed") {
          true_mug = true;
        }
      }
    }

    if (true_mug && mug_vt_id != "") {
      items.push(mug);
    }

    // document.querySelector('.atc-btn').classList.toggle("loading", true);

    // Disable page interactions during add to cart
    setPageInteractionBlocked('add-to-cart', true);
        
    fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            items,
            sections
        })
    })
    .then(res => res.json())
    .then(res => {
        let step_header = document.querySelector('.step-header-wrapper').outerHTML;
        let step_header_style = document.querySelector('#step-header-style').outerHTML;
        localStorage.setItem('step_header', step_header);
        localStorage.setItem('step_header_style', step_header_style);
        markStepBuilderForCheckoutReset();
        window.location.href = window.location.origin + '/checkout';
    })
    .catch(err => {
        console.log(err);
        // Re-enable page interactions on error
        setPageInteractionBlocked('add-to-cart', false);
    });

  }

  /**
   * Recomputes the subtotal, free-shipping message, and compare-at display for current items.
   *
   * @returns {void}
   */
  updateSubTotal() {
    let subTotal = 0;
    let symbol = this.cur_symbol;
    let rate = parseFloat(window.Shopify.currency.rate);
    let variantId;

    for (const item of this.items) {
      subTotal += parseInt(item.price);
      variantId = item.id;
    }

    const freeShippingThresholdCents = window.__themeFreeShippingThresholdCents ?? 10000;
    try {
      this.querySelector('.subtotal .money').setAttribute('data-variant-id',variantId);
      this.querySelector('.subtotal .money').innerHTML = `${symbol}${(subTotal / 100 ).toFixed(2)}`;
      if (subTotal < freeShippingThresholdCents) {
        this.querySelector('.step-freeshipping').innerHTML = `<p>You're only <span class="money">${symbol}${((freeShippingThresholdCents - subTotal)/100).toFixed(2)}</span> away from <strong>FREE SHIPPING</strong></p>`;
      } else {
        this.querySelector('.step-freeshipping').innerHTML = "<p>Congrats! You've earned <strong>FREE SHIPPING</strong></p>";
      }

      this.updateWasPrice();
    } catch (error) {
      console.error(error);
    }
  }
}

customElements.define('step-block', StepBlock);

/**
 * Selectable variant option used inside the option drawer UI.
 *
 * Expects `data-option-name` and `data-option-value` so parent containers can reconstruct
 * a full Shopify variant selection and refresh the drawer summary.
 */
class VariantOption extends HTMLElement {
  /**
   * Registers the option-change event emitted when a drawer option is selected.
   */
  constructor() {
    super();
    this.updateOtionEv = new Event('option:change', {bubbles: true});

    this.addEventListener('click', () => {
      this.parentElement.querySelectorAll('variant-option').forEach(sibling => sibling.classList.toggle('selected', false));
      this.classList.toggle('selected', true);
      this.dispatchEvent(this.updateOtionEv);
    });
  }

}

customElements.define('variant-option', VariantOption);

/**
 * Drawer section controller for a single configurable product option.
 *
 * It keeps the drawer's temporary selection state in sync with the visible summary card,
 * opens/closes the appropriate panel, and notifies `option-drawer` when a variant option changes.
 *
 * Expects `data-option` and `data-option-name`, plus matching `.design-option` markup elsewhere
 * in the DOM that reflects the currently selected value.
 */
class OptionContainer extends HTMLElement {
  /**
   * Connects a drawer option section to its mirrored summary tile and open/close controls.
   */
  constructor() {
    super();
    let target = this.dataset.option;
    this.targetEl = document.querySelector(`.design-option[data-option="${target}"]`);
    this.opener = this.targetEl.querySelector('.option-choose').parentElement;
    this.closer = document.querySelector('option-drawer .before-step');
    this.parent = this.parentElement;
    this.addActions();
  }

  /**
   * Snapshots the currently selected drawer option into a normalized object used by the summary UI.
   *
   * @returns {void}
   */
  init() {
    this.selectedEl = this.querySelector('variant-option.selected');
    let detailEl = this.selectedEl?.querySelector('[data-option-detail]'); 
    let imageEl = this.selectedEl?.querySelector('img'); 
  
    this.curOption = {
      title: this.selectedEl.dataset.optionValue,
      detail: detailEl?.innerHTML,
      image: imageEl?.src
    };
  }

  /**
   * Binds drawer open/close behavior and listens for variant-option updates.
   *
   * @returns {void}
   */
  addActions() {
    this.addEventListener('option:change', this.updateCurOption.bind(this));
    this.opener.addEventListener('click', () => {
      this.open();
    });
    this.closer.addEventListener('click', () => {
      this.close();
    });
  }

  /**
   * Updates the cached option snapshot after a drawer option selection changes.
   *
   * @param {CustomEvent|Event} ev
   * @returns {void}
   */
  updateCurOption(ev) {
    this.curOption.title = ev.target.dataset.optionValue;

    ev.target.querySelector('[data-detail]') ? this.curOption.detail = ev.target.querySelector('[data-detail]').innerHTML : null;

    ev.target.querySelector('img') ? this.curOption.image = ev.target.querySelector('img').src : null;
  }

  /**
   * Renders the cached option snapshot back into the summary tile UI.
   *
   * @returns {void}
   */
  updateUi() {
    this.curOption?.title ? this.targetEl.querySelector('.option-title').innerHTML = this.curOption.title : null;
    this.curOption?.detail ? this.targetEl.querySelector('.option-detail').innerHTML = this.curOption.detail : null;
    this.curOption?.image ? this.targetEl.querySelector('.option-image').querySelector('img').setAttribute("src", this.curOption.image) : null;
  }

  /**
   * Opens this option section inside the shared drawer and syncs the summary preview first.
   *
   * @returns {void}
   */
  open() {
    let target = this.dataset.option;
    this.init();
    this.updateUi();
    this.parentElement.scrollTop = 0;
    let optionName = this.dataset.optionName;
    window.productData.options.forEach((option, arg) => {
      if(option == optionName) {
        document.querySelector('option-drawer').updatePremium(arg);
      }
    });
    this.parent.dataset.option = target;
    document.querySelector('option-drawer').dataset.option = target;
  }
  
  /**
   * Closes this option section and restores the drawer to its default frame summary view.
   *
   * @returns {void}
   */
  close() {
    this.init();
    this.updateUi();
    this.parent.dataset.option = 'frame';
    document.querySelector('option-drawer').dataset.option = 'frame';
  }
}

customElements.define('option-container', OptionContainer);

/**
 * Summary panel renderer for the multi-selection design flow.
 *
 * It reads the current catalog/upload state from the page, mirrors the selected variants and
 * uploaded media into a compact summary UI, and wires edit/delete affordances back to the
 * corresponding interactive controls.
 */
class SummaryDesign extends HTMLElement {
  /**
   * Captures product data needed to render the summary list for multi-design flows.
   */
  constructor() {
    super();
    this.product = window.productData;
    this.cur_symbol = document.querySelector('#currency_symbol').textContent;
  }

  /**
   * Rebuilds the summary cards from the active catalog/design selections on the page.
   *
   * @returns {void}
   */
  init() {
    let cur_symbol = this.cur_symbol;
    
    document.querySelector('option-drawer .summary-count .count').innerHTML = ms_num;
    this.querySelector('.summary-delete').classList.toggle('hidden', ms_num == 1);
    document.querySelectorAll('ms-catalog').forEach((catalog, arg) => {
      const target = this.querySelector(`.summary-item:nth-child(${arg + 2})`);
      if (catalog.className.includes('active')) {
        target.classList.toggle('active', true);
        let v_id = catalog.vt_id;
        const curVaraint = this.product.variants.find( variant => variant.id == v_id);

        target.querySelector('.summary-media').style.backgroundImage = `url(${catalog.upload_prop_direct_val})`;
        target.querySelector('.summary-title').innerHTML = this.product.title;
        for (let i = 0; i < this.product.options.length; i++) {
          target.querySelector(`.summary-option[data-option="${this.product.options[i]}"] .option-value`).innerHTML = curVaraint.options[i];
        }
        target.querySelector('.summary-price'). innerHTML = `${cur_symbol}${(curVaraint.price / 100).toFixed(2)}`;
        target.querySelector('.summary-edit').addEventListener('click', () => {
          if(catalog.className.includes('uploaded')) {
            catalog.querySelector('.edit-btn').dispatchEvent(new MouseEvent('click'));
          } else {
            document.querySelector('option-drawer').close();
            catalog.querySelector('.ms-photo-wrapper').dispatchEvent(new MouseEvent('click'));
            
          }
        });
        target.querySelector('.summary-delete').addEventListener('click', () => {
          let parent = target.parentElement;
          const index = Array.prototype.indexOf.call(parent.children, target);
          document.querySelector('.remove-modal').dataset.index = index;
          document.querySelector('.remove-modal').classList.remove('hidden');
        });
      }
      else {
        target.classList.toggle('active', false);
      }
    });
  }
}

customElements.define('summary-design', SummaryDesign);
/**
 * Mobile/overlay drawer controller for browsing and applying design option combinations.
 *
 * Responsibilities include opening and closing the drawer, mirroring current variant state,
 * applying selections back to the main product builder, and handling the drawer-level checkout action.
 */
class OptionDrawer extends HTMLElement {
  /**
   * Initializes drawer state, pricing references, and action controls.
   */
  constructor() {
    super();
    this.product = window.productData;
    this.curVaraint = '';
    this.vt_id = '';
    this.price = '';
    this.curOption = '';
    this.target = '';
    this.cur_symbol = document.querySelector('#currency_symbol').textContent;
    this.applyer = this.querySelector('.apply-design');
    this.closer = this.querySelector('.drawer__close');
    this.atc = this.querySelector('.proceed-checkout a');
    this.addActions();
  }

  /**
   * Binds drawer overlay, close, apply, and checkout actions.
   *
   * @returns {void}
   */
  addActions() {
    this.closer.addEventListener('click', () => {
      this.close();
    });

    this.addEventListener('click', (e) => {
      const overlayClicked = this.querySelector('.design-container').contains(e.target);
      overlayClicked ? null : this.close();
    });

    this.addEventListener('option:change', this.updateVariant.bind(this));

    this.applyer.addEventListener('click', () => {
      this.applyDesign();
    });
    this.atc.addEventListener('click', (ev) => {
      ev.preventDefault();
      const items = this.items;
      fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            items
        })
      })
      .then(res => res.json())
      .then(res => {
        markStepBuilderForCheckoutReset();
        window.location.href = '/checkout';
      });
    });
  }

  /**
   * Syncs drawer option selections from the currently active variant combination.
   *
   * @returns {void}
   */
  updateOptions() {
    const curOption = this.curOption.split(',');
    this.querySelectorAll('variant-option').forEach(option => {
      if(curOption.includes(option.dataset.optionValue)) {
        option.classList.toggle('selected', true);
      } else {
        option.classList.toggle('selected', false);
      }
    });
    this.querySelectorAll('option-container').forEach(container => {
      container.init();
      container.updateUi();
    });
  }

  /**
   * Recomputes the drawer's current variant candidate after an option change.
   *
   * @param {CustomEvent|Event} ev
   * @returns {void}
   */
  updateVariant(ev) {
    let optionName = ev.target.dataset.optionName;
    let optionValue = ev.target.dataset.optionValue;
    this.product.options.forEach((option, arg) => {
      if(option == optionName) {
        let curOption = this.curOption.split(',');
        curOption[arg] = optionValue;
        this.curOption = curOption.join(',');

        const curVaraint = this.product.variants.find(variant => {
          return variant.options.join(',') == curOption.join(',');
        });
        this.vt_id = curVaraint.id;
        this.price = curVaraint.price;
        this.updatePremium(arg);
        this.target.price = this.price;
        this.target.vt_id = this.vt_id;
        this.target.classList.add('designed');
        this.updateSubTotal();
      }
    });
  }

  /**
   * Updates the drawer premium messaging for the option index currently in focus.
   *
   * @param {number} index
   * @returns {void}
   */
  updatePremium(index) {
    let cur_symbol = this.cur_symbol;
    let curOption = this.curOption.split(',');
    const subVariants = this.product.variants.filter( variant => {
      let isPassed = true;
      let i = 0;

      do {
        if( i != index) {
          variant.options[i] === curOption[i] ? isPassed = true : isPassed = false;
        }
        if(!isPassed) return isPassed;
        i++;
      } while (i < curOption.length);
      return isPassed
    });

    let premiumPrices = {};
    let curPrice = this.price;

    subVariants.forEach( variant => {
      let delta = variant.price - curPrice;
      Object.assign(premiumPrices, {[variant.options[index]]: delta});
    });
    let target = this.product.options[index].toLowerCase();
     
    for (const key in premiumPrices) {
      premiumPrices[key] > 0 ? this.querySelector(`variant-option[data-option-value='${key}'] .premium-price`).innerHTML = `+${cur_symbol}${(premiumPrices[key] / 100).toFixed(2)}` : this.querySelector(`variant-option[data-option-value='${key}'] .premium-price`).innerHTML = null;
    }
  }

  /**
   * Recomputes the drawer subtotal display for the selected design set.
   *
   * @returns {void}
   */
  updateSubTotal() {
    let subTotal = count_subtotal_price();
    let originalTotal = count_original_price ();
    let saveTotal = count_saving_price();
    try {
      this.querySelector('.design-header .summary-price').innerHTML = `${this.cur_symbol}${subTotal.toFixed(2)}`;
      this.querySelector('.design-footer .summary-price').innerHTML = `${this.cur_symbol}${subTotal.toFixed(2)}`;
      this.querySelector('.total-price').innerHTML = `${this.cur_symbol}${originalTotal.toFixed(2)}`;
      let reward = '';
      const freeShippingThresholdDollars = (window.__themeFreeShippingThresholdCents ?? 10000) / 100;
      this.querySelector('#reward-content span[data-free-shipping]').classList.toggle('hidden', subTotal < freeShippingThresholdDollars);
  
      reward = `You're Saving ${this.cur_symbol}${(saveTotal).toFixed(2)}`;
      this.querySelector('#reward-content span[data-saving]').innerHTML = reward;
    } catch (error) {
      console.error(error);
    }
  }
  /**
   * Applies the drawer's selected variant configuration back to the source catalog item.
   *
   * @returns {void}
   */
  applyDesign() {
    this.target.price = this.price;
    this.target.vt_id = this.vt_id;
    this.target.classList.add('designed');
    update_designed_num();
    if(this.dataset.option == 'frame') {
      this.close();
    } 
    else {
      this.querySelectorAll('option-container').forEach(container => {
        container.close();
      });
    }
    update_subtotal();
  }

  /**
   * Opens the drawer for a specific catalog/design item and initializes its current selections.
   *
   * @param {HTMLElement} el Source element that owns the design data.
   * @param {string} [step='frame'] Initial drawer subsection to show.
   * @param {Array|null} [items=null] Optional line items used by the drawer checkout flow.
   * @returns {void}
   */
  open(el, step='frame', items=null) {
    el ? null : el = document.querySelector('ms-catalog.active');
    const parent = document.querySelector('.ms-catalog-wrapper');
    const index = Array.prototype.indexOf.call(parent.children, el);
    const total = document.querySelectorAll('ms-catalog.active').length;
    document.querySelector('option-drawer .edit-tile').innerHTML = `${index + 1} / ${total}`;
    
    if(step == 'summary') {
      this.items = items;
      document.querySelector('summary-design').init();
      this.querySelector('.design-body').scrollTop = 0;
    } 
    this.dataset.option = step;
    this.target = el;
    this.vt_id = el.vt_id;
    this.price = el.price;
    let imgUrl = el.upload_prop_direct_val;
    this.querySelector('.design-preview .image-wrapper').style.backgroundImage = `url(${imgUrl})`;

    this.product.variants.forEach( variant => {
      if(variant.id == this.vt_id) {
        let options = variant.options;
        this.curOption = options.join(',');
      }
    });
    this.updateOptions();
    this.updateSubTotal();
    this.classList.toggle('open', true);
    document.querySelector('body').classList.toggle('option-drawer', true);
  }

  /**
   * Closes the design drawer and restores its default state.
   *
   * @returns {void}
   */
  close() {
    this.dataset.option = 'frame';
    this.classList.toggle('open', false);
    document.querySelector('body').classList.toggle('option-drawer', false);
  }
}

customElements.define('option-drawer', OptionDrawer);

